<template>
  <div class="ppe">
    <loading-panel :loading="loading"></loading-panel>
    <base-alert-dialog
      :show="confirmDialog.show"
      :variant="confirmDialog.variant"
      :title="confirmDialog.title"
      :message="confirmDialog.message"
      :confirm-text="confirmDialog.confirmText"
      :cancel-text="confirmDialog.cancelText"
      :confirm-variant="confirmDialog.confirmVariant"
      show-cancel
      @confirm="onConfirmDialogConfirm"
      @cancel="onConfirmDialogCancel"
    />

    <!-- Web font loaders for live preview -->
    <div v-for="(f, i) in orderedFonts" :key="'fontlink-' + i" style="display: none">
      <link rel="stylesheet" :href="'https://fonts.googleapis.com/css?family=' + encodeURIComponent(f.family)">
    </div>

    <div v-if="!loading">
      <!-- Breadcrumb -->
      <breadcrumb v-if="!embedded">
        <breadcrumb-item>
          <a href="#" @click.prevent="$router.push('/purchases/point-of-sale')">Point of sale</a>
        </breadcrumb-item>
        <breadcrumb-item>
          <span>{{ headerTitle }}</span>
        </breadcrumb-item>
      </breadcrumb>

      <div class="row no-gutters ppe__layout">
        <!-- ─── Editor card ──────────────────────────────────────────── -->
        <!-- Editor takes full width while the LIVE PREVIEW column is hidden
             (see the commented-out preview block below). Flip back to
             col-lg-9 when we restore the preview. -->
        <div class="col-lg-12 col-12">
          <component
            :is="embedded ? 'div' : 'card'"
            class="ppe__editor mb-0"
            :class="{ 'ppe__editor--embedded': embedded }"
            v-bind="embedded ? {} : { 'card-body-classes': 'ppe__card-body' }"
          >
            <!-- Header row: title + badges + actions. Not shown when
                 embedded in Payment settings: the tab is the title. -->
            <div v-if="!embedded" slot="header" class="ppe__header">
              <div class="ppe__header-title">
                <h4 class="text-white mb-0 ppe__title">{{ headerTitle }}</h4>
                <div class="ppe__header-meta">
                  <template v-if="!embedded">
                    <badge :type="typeBadgeType">{{ typeLabel }}</badge>
                    <badge v-if="!isNew && page.type !== 'pos' && page.is_default === 1" type="info" class="ml-1">main</badge>
                    <badge v-if="!isNew && page.archived === 1" type="warning" class="ml-1">archived</badge>
                    <badge v-else-if="!isNew" type="success" class="ml-1">live</badge>
                  </template>
                  <small v-if="!isNew && page.updated_at" class="ppe__edited">
                    Edited {{ relative(page.updated_at) }}
                  </small>
                </div>
              </div>
              <div class="ppe__header-actions">
                <el-dropdown v-if="!isNew && !embedded">
                  <base-button link type="primary" size="sm">
                    <i style="font-size: large" class="tim-icons el-icon-more"></i>
                  </base-button>
                  <template #dropdown>
                    <el-dropdown-menu>
                      <a v-if="page.type !== 'pos' && page.is_default !== 1" class="dropdown-item mt-1 mb-2" href="#" @click.prevent="handleAction('setDefault')">
                        <i class="tim-icons icon-check-2 mr-2"></i> Set as main
                      </a>
                      <a class="dropdown-item mt-1 mb-2" href="#" @click.prevent="handleAction('duplicate')">
                        <i class="tim-icons icon-single-copy-04 mr-2"></i> Duplicate
                      </a>
                      <hr>
                      <a v-if="page.archived !== 1" class="dropdown-item mt-1 mb-2" href="#" @click.prevent="handleAction('archive')">
                        <i class="tim-icons icon-trash-simple mr-2"></i> Archive
                      </a>
                      <a v-else class="dropdown-item mt-1 mb-2" href="#" @click.prevent="handleAction('restore')">
                        <i class="tim-icons icon-refresh-02 mr-2"></i> Restore
                      </a>
                    </el-dropdown-menu>
                  </template>
                </el-dropdown>
              </div>
            </div>

            <!-- Two-column inside: nav left, content right -->
            <div class="ppe__split">
              <aside class="ppe__nav">
                <editor-section-nav
                  :sections="sections"
                  :active-section="activeSection"
                  @navigate="setSection"
                />
              </aside>

              <div class="ppe__content">
                <!-- ═══ General ════════════════════════════════════════ -->
                <div v-if="activeSection === 'general'" :key="'tab-general'">
                  <!-- Identity is page-list housekeeping; the single payment
                       request template has nothing to identify. -->
                  <collapsible-section v-if="!embedded" title="Identity" icon="tim-icons icon-settings">
                    <div class="ppe__row">
                      <div class="ppe__col">
                        <base-input
                          :error="errs.name"
                          required
                          label="Page name"
                          v-model="page.name"
                          placeholder="e.g. Corporate, Retail, VIP"
                        />
                        <p class="ppe__hint">Internal name. Customers see the page title instead.</p>
                      </div>
                      <div class="ppe__col">
                        <label class="text-white">Surface type</label>
                        <!-- POS-only beta: type is fixed. The selector returns
                             when invoicing (type 'link') ships as its own feature. -->
                        <p class="ppe__type-readonly">
                          <i class="tim-icons ppe__type-readonly-icon" :class="typeIconClass"></i>
                          {{ typeLabel }}
                        </p>
                      </div>
                    </div>

                    <!-- "Default" only governs which page a payment request
                         falls back to when none is specified. POS devices
                         each bind to exactly one page (pos_devices.payment_page_id),
                         so a POS page is never resolved by default — hide it. -->
                    <div v-if="page.type !== 'pos' && !embedded" class="ppe__inline-toggle">
                      <el-switch
                        v-model="page.is_default"
                        :active-value="1"
                        :inactive-value="0"
                        active-color="#e14eca"
                      />
                      <label class="text-white mb-0">Default for {{ typeLabel.toLowerCase() }}</label>
                    </div>
                  </collapsible-section>

                  <!-- Devices (POS templates). Always shown for discoverability
                       — a first-time user should see that tablets/readers pair
                       here and that an app exists, even before the page is
                       created. Pairing binds to a saved page id, but rather
                       than block with a "save first" message, Add device on a
                       new page saves the page transparently and then opens the
                       pair form (see onAddDeviceCommand). Opens by default on a
                       new page. -->
                  <collapsible-section
                    v-if="page.type === 'pos'"
                    ref="devicesSection"
                    title="Devices"
                    icon="tim-icons icon-tablet-2"
                    :badge="devices.length ? String(devices.length) : ''"
                    :default-open="isNew"
                  >
                      <info-hint variant="primary">
                        Devices that take payments for this page. Each one runs the
                        <strong>OTC POS app</strong>: a phone, a tablet, or a Stripe S710 with the app installed.
                        Stripe readers without the app are registered under Payment settings and picked
                        directly in Send to POS.
                      </info-hint>

                      <!-- Loading skeleton -->
                      <div v-if="devicesLoading" class="ppe__readers-skel">
                        <span class="skel" style="display:block; width: 60%; height: 14px; margin-bottom: 8px;"></span>
                        <span class="skel" style="display:block; width: 100%; height: 36px;"></span>
                      </div>

                      <template v-else>
                        <!-- Empty state — no devices on this page yet. -->
                        <div
                          v-if="devices.length === 0 && !pairForm.open"
                          class="ppe__devices-empty"
                        >
                          <i class="tim-icons icon-tablet-2 ppe__devices-empty-icon"></i>
                          <p class="ppe__devices-empty-title">No devices on this page yet</p>
                          <p class="ppe__devices-empty-hint">
                            Pair a phone, tablet, or S710 running the OTC POS app to start taking
                            in-person payments here.
                          </p>
                          <base-button type="primary" size="sm" style="margin-top: 14px;" @click="onAddDeviceCommand('tablet')">
                            <i class="tim-icons icon-simple-add mr-1"></i>
                            Add device
                          </base-button>
                        </div>

                        <!-- Device list: app devices only (rename + status
                             treatment). Readers are never rows here. -->
                        <ul v-if="devices.length > 0" class="ppe__devices-list">
                          <li
                            v-for="d in devices"
                            :key="`dev-${d.id}`"
                            class="ppe__device-row"
                          >
                            <!-- App device row -->
                              <div class="ppe__device-row-body">
                                <div
                                  v-if="editingDeviceId !== d.id"
                                  class="ppe__device-name-display"
                                  role="button"
                                  tabindex="0"
                                  @click="startEditDeviceName(d)"
                                  @keydown.enter="startEditDeviceName(d)"
                                >
                                  <span class="ppe__device-name">{{ d.name }}</span>
                                  <i class="tim-icons icon-pencil ppe__device-name-pencil"></i>
                                </div>
                                <div v-else class="ppe__device-name-edit">
                                  <div class="ppe__device-name-input-wrap">
                                    <input
                                      :ref="`deviceNameInput-${d.id}`"
                                      v-model="editingDeviceName"
                                      class="ppe__device-name-input"
                                      maxlength="80"
                                      placeholder="Device name"
                                      @blur="saveDeviceName(d)"
                                      @keydown.enter.prevent="$event.target.blur()"
                                      @keydown.esc="cancelEditDeviceName"
                                    />
                                    <kbd class="ppe__device-name-kbd" aria-hidden="true">↵</kbd>
                                  </div>
                                  <button
                                    type="button"
                                    class="ppe__device-name-icon ppe__device-name-icon--save"
                                    aria-label="Save name"
                                    title="Save"
                                    @mousedown.prevent
                                    @click="saveDeviceName(d)"
                                  >
                                    <i class="tim-icons icon-check-2"></i>
                                  </button>
                                  <button
                                    type="button"
                                    class="ppe__device-name-icon"
                                    aria-label="Cancel"
                                    title="Cancel"
                                    @mousedown.prevent
                                    @click="cancelEditDeviceName"
                                  >
                                    <i class="tim-icons icon-simple-remove"></i>
                                  </button>
                                </div>
                                <p class="ppe__device-meta">
                                  <span class="ppe__device-type-chip">POS app</span>
                                  <span class="ppe__device-dot">·</span>
                                  <span
                                    class="ppe__device-status"
                                    :class="`ppe__device-status--${displayStatus(d)}`"
                                  >
                                    {{ deviceStatusLabel(displayStatus(d)) }}
                                  </span>
                                  <span class="ppe__device-dot">·</span>
                                  <span>{{ lastSeenLabel(d.last_seen_at) }}</span>
                                  <span class="ppe__device-dot">·</span>
                                  <el-dropdown trigger="click">
                                    <span class="ppe__device-autolock" role="button" tabindex="0">
                                      Auto-lock: {{ autoLockLabel(d.auto_lock_timeout_minutes) }}
                                      <i class="tim-icons icon-minimal-down"></i>
                                    </span>
                                    <template #dropdown>
                                      <el-dropdown-menu>
                                        <a
                                          v-for="opt in autoLockOptions"
                                          :key="`al-${opt.value}`"
                                          class="dropdown-item mt-1 mb-2"
                                          href="#"
                                          @click.prevent="saveDeviceAutoLock(d, opt.value)"
                                        >
                                          {{ opt.label }}
                                        </a>
                                      </el-dropdown-menu>
                                    </template>
                                  </el-dropdown>
                                </p>
                              </div>
                              <div class="ppe__device-row-actions">
                                <base-button link size="sm" type="primary" @click="revokeDevice(d)">
                                  Revoke
                                </base-button>
                              </div>
                          </li>
                        </ul>

                        <!-- "Add device" CTA — visible when list has rows AND
                             the pair form is closed. -->
                        <div
                          v-if="devices.length > 0 && !pairForm.open"
                          class="ppe__devices-add-row"
                        >
                          <base-button type="primary" size="sm" @click="onAddDeviceCommand('tablet')">
                            <i class="tim-icons icon-simple-add mr-1"></i>
                            Add device
                          </base-button>
                        </div>

                        <!-- Pair form (tablet) -->
                        <div v-if="pairForm.open" class="ppe__pair-form">
                          <p class="ppe__pair-form-title">Pair OTC POS tablet</p>

                          <div class="ppe__pair-form-name">
                            <label class="text-white">Device name</label>
                            <base-input
                              v-model="pairForm.deviceName"
                              placeholder="e.g. Front counter, Bar POS"
                            />
                            <p class="ppe__hint">Optional. Helps you identify this tablet in the list.</p>
                          </div>

                          <div class="ppe__pair-form-code">
                            <p class="ppe__pair-code-label">Pairing code</p>
                            <p v-if="pairForm.loading" class="ppe__pair-code-value ppe__pair-code-value--loading">
                              ● ● ● ● ● ●
                            </p>
                            <p v-else-if="pairForm.error" class="ppe__pair-code-error">
                              {{ pairForm.error }}
                            </p>
                            <template v-else>
                              <p class="ppe__pair-code-value">{{ formattedCode }}</p>
                              <p class="ppe__pair-code-countdown" :class="{ 'ppe__pair-code-countdown--low': pairForm.expiresIn < 60 }">
                                <span v-if="pairForm.expiresIn > 0">Expires in {{ formattedCountdown }}</span>
                                <span v-else>Code expired</span>
                              </p>
                            </template>
                          </div>

                          <p class="ppe__hint">
                            Open the OTC POS app on the tablet and enter this code.
                            The device will appear in the list above once paired.
                          </p>

                          <div class="ppe__pair-form-actions">
                            <base-button type="secondary" size="sm" @click="cancelPairForm">
                              Cancel
                            </base-button>
                            <base-button
                              type="primary"
                              size="sm"
                              :disabled="pairForm.loading"
                              @click="regeneratePairCode"
                            >
                              <i class="tim-icons icon-refresh-01 mr-1"></i>
                              Generate new code
                            </base-button>
                          </div>
                        </div>

                      </template>
                  </collapsible-section>

                  <!-- Payment device setup — sibling to Devices. Shown for POS
                       regardless of save state: page-agnostic, and a venue
                       wiring up in-person payments wants it at creation time.
                       Two paths: a phone/tablet download, and the Stripe Reader
                       S710. A venue whose connected Stripe account is platform
                       controlled gets the app pushed to its readers by OTC's
                       deploy group; every other venue (API keys, or connected
                       without control) self-deploys through its own Stripe
                       Apps on Devices, so the block branches on
                       stripePlatformControlled (from the readers response).
                       Opens by default on a new page. -->
                  <collapsible-section
                    v-if="page.type === 'pos'"
                    title="Set up a payment device"
                    icon="tim-icons icon-cloud-download-93"
                    :default-open="isNew"
                  >
                    <div class="ppe__app-section">
                      <info-hint variant="primary">
                        Run the OTC POS app on a phone or tablet, or on a Stripe
                        Reader S710, to take in-person payments with Tap to Pay.
                        Choose your device below.
                      </info-hint>

                      <!-- Phone / tablet: download and pair. QR is the primary
                           affordance (scanning beats typing a URL on the
                           device); the button stays for desktop downloads.
                           --first is an explicit modifier: :first-of-type
                           can't work here (the info-hint above is also a div,
                           so the first device block is never first-of-type). -->
                      <div class="ppe__app-device ppe__app-device--first">
                        <h6 class="ppe__app-device-title">Phone or tablet</h6>
                        <p class="ppe__app-device-text">
                          Scan the code with the device's camera to download the
                          app on an Android 11+ phone or tablet with NFC, then
                          pair it under <strong>Devices</strong>. Before tapping
                          the APK, enable <em>Install unknown apps</em> for the
                          browser you used to download it.
                        </p>
                        <div class="ppe__app-download">
                          <div class="ppe__app-qr-tile">
                            <qrcode-vue :value="posAppDownloadUrl" :size="104" level="M" />
                          </div>
                          <span class="ppe__app-qr-caption">Scan to download</span>
                          <base-button
                            tag="a"
                            :href="posAppDownloadUrl"
                            target="_blank"
                            rel="noopener"
                            type="primary"
                            size="sm"
                          >
                            <i class="tim-icons icon-cloud-download-93 mr-1"></i>
                            Download for Android
                          </base-button>
                        </div>
                      </div>

                      <!-- Stripe Reader S710, platform-controlled account: OTC
                           deploys the app; the venue only registers and pairs. -->
                      <div v-if="stripePlatformControlled === 1" class="ppe__app-device">
                        <h6 class="ppe__app-device-title">Stripe Reader S710</h6>
                        <p class="ppe__app-device-text">
                          Your Stripe account is connected to OTC with platform
                          control, so the OTC POS app is installed on your readers
                          for you. Nothing to upload or deploy in Stripe.
                        </p>
                        <ol class="ppe__app-steps">
                          <li>
                            Register the S710 under
                            <strong>Purchases &gt; Payment settings &gt; Payment gateways &gt; Hardware</strong>
                            with <strong>Connect a reader</strong>. The reader
                            downloads the app and reboots to install it.
                          </li>
                          <li>
                            Back here, pair the reader under
                            <strong>Devices</strong>.
                          </li>
                        </ol>
                      </div>

                      <!-- Stripe Reader S710, API keys or connected without
                           platform control: self-deploy via the venue's own
                           Stripe Apps on Devices. -->
                      <div v-else class="ppe__app-device">
                        <h6 class="ppe__app-device-title">Stripe Reader S710</h6>
                        <p class="ppe__app-device-text">
                          The S710 runs through Stripe's Apps on Devices, so it
                          is set up from your own Stripe Dashboard. You do this
                          once per reader.
                        </p>
                        <ol class="ppe__app-steps">
                          <li>
                            Ask your Stripe representative to enable Apps on
                            Devices on your account. This is required before any
                            app can be deployed to an S710.
                          </li>
                          <li>
                            In your Stripe Dashboard, register your S710 to a
                            location under Terminal.
                          </li>
                          <li>
                            Download the app below, then upload it under Software
                            in your Stripe Dashboard. When Stripe asks for app
                            information, enter <strong>OTC POS</strong> as the app
                            name and <code>io.offthecouch.pos</code> as the
                            package name. Submit it for Stripe's review. Stripe
                            emails you when it is approved.
                          </li>
                          <li>
                            Under Software, create a deploy group with device
                            type Android, then add the location your reader
                            belongs to.
                          </li>
                          <li>
                            Open the OTC POS app, click Deploy version, choose
                            the approved version, set OTC POS as the startup app,
                            and deploy. Your reader downloads it and reboots to
                            install.
                          </li>
                          <li>
                            Back in OTC, pair the reader under
                            <strong>Devices</strong>.
                          </li>
                        </ol>
                        <info-hint variant="warning" class="mb-3">
                          Make sure the Stripe account connected to OTC is the
                          same live account your reader is registered to. A
                          mismatch is the most common reason a reader will not
                          connect.
                        </info-hint>
                        <div class="ppe__app-section-actions">
                          <base-button
                            tag="a"
                            :href="posS710AppDownloadUrl"
                            target="_blank"
                            rel="noopener"
                            type="primary"
                            size="sm"
                          >
                            <i class="tim-icons icon-cloud-download-93 mr-1"></i>
                            Download for S710
                          </base-button>
                        </div>
                      </div>
                    </div>
                  </collapsible-section>

                </div>

                <!-- ═══ Content ════════════════════════════════════════ -->
                <div v-if="activeSection === 'content'" :key="'tab-content'">
                  <!-- Same underlying fields for both page types; the POS
                       app renders title/subtext on the device's ready
                       screen and confirmation_message on its success
                       screen, so the section name + hints adapt. -->
                  <collapsible-section
                    :title="page.type === 'pos' ? 'Device screens' : 'Page content'"
                    icon="tim-icons icon-paper"
                  >
                    <base-input
                      :label="page.type === 'pos' ? 'Ready screen title' : 'Page title'"
                      v-model="page.title"
                      :placeholder="page.type === 'pos' ? 'Welcome to Escape HQ' : 'Complete your payment'"
                    />
                    <info-hint variant="primary">
                      {{ page.type === 'pos'
                        ? "Headline on the device's ready screen. Blank shows \"Ready\"."
                        : 'Main heading on the customer-facing checkout. Blank uses the venue name.' }}
                    </info-hint>

                    <!-- Bare <label> + ppe__label spacing so all three field
                         labels render identically to base-input's. -->
                    <label class="ppe__label">{{ page.type === 'pos' ? 'Ready screen subtext' : 'Subtext' }}</label>
                    <textarea
                      v-model="page.subtext"
                      class="ppe__textarea"
                      rows="2"
                      :placeholder="page.type === 'pos' ? 'Pay here with tap, chip, or phone.' : 'Thanks for booking with us.'"
                    ></textarea>
                    <info-hint variant="primary">
                      {{ page.type === 'pos'
                        ? 'Supporting line beneath the headline. Blank shows "Waiting for next order".'
                        : 'Optional supporting copy beneath the title.' }}
                    </info-hint>

                    <label class="ppe__label">{{ page.type === 'pos' ? 'Success screen message' : 'Confirmation message' }}</label>
                    <textarea
                      v-model="page.confirmation_message"
                      class="ppe__textarea"
                      rows="3"
                      placeholder="We've received your payment."
                    ></textarea>
                    <info-hint variant="primary">
                      {{ page.type === 'pos'
                        ? "Shown on the device's success screen after payment."
                        : 'Shown after a successful payment.' }}
                    </info-hint>
                  </collapsible-section>

                  <collapsible-section
                    title="Custom fields"
                    icon="tim-icons icon-notes"
                    :default-open="false"
                    :badge="linkedCustomFields.length ? String(linkedCustomFields.length) : ''"
                  >
                    <!-- Shared with BookingSiteSettings' customer-info
                         section (CustomFieldLinkCards). Toggles edit the
                         link row (custom_field_payment_page_link.required
                         /interaction_required/add_alert), staged with the
                         page save. -->
                    <custom-field-link-cards
                      :items="linkedCustomFields"
                      :addable="addableCustomFields"
                      :toggles="customFieldToggles"
                      remove-tooltip="Remove from this page"
                      @add="addCustomField"
                      @remove="removeCustomField"
                      @toggle="setCustomFieldFlag"
                    />
                  </collapsible-section>

                  <!-- PARKED (2026-06-10): signature feature hidden — card
                       networks dropped signature requirements in 2018, so
                       it's ceremony with no dispute value and nobody asked
                       for it. payment_pages.signature_required stays in the
                       schema; the device's Signature screen + capture
                       plumbing remain in otc-pos-app, with Tap routing
                       bypassed. Re-enable by restoring this section and the
                       Tap.tsx routing. -->
                  <collapsible-section
                    v-if="false"
                    title="Signature"
                    icon="tim-icons icon-pencil"
                    :default-open="false"
                  >
                    <toggle-field
                      v-model="page.signature_required"
                      label="Require signature"
                    />
                    <info-hint variant="primary">
                      Customer signs on screen before completing the payment.
                    </info-hint>
                  </collapsible-section>

                  <collapsible-section
                    v-if="page.type === 'pos'"
                    title="Line items"
                    icon="tim-icons icon-bullet-list-67"
                    :default-open="false"
                  >
                    <toggle-field
                      v-model="page.show_line_items"
                      label="Show line items"
                    />
                    <info-hint variant="primary">
                      Show itemized cart on the customer-facing screen. Off shows the total only.
                    </info-hint>
                  </collapsible-section>

                  <collapsible-section
                    v-if="page.type === 'pos'"
                    title="Receipt screen"
                    icon="tim-icons icon-email-85"
                    :default-open="false"
                    :badge="(page.receipt_email_on_file === 1 || page.receipt_custom_email === 1) ? 'On' : 'Off'"
                  >
                    <info-hint variant="primary">
                      Which receipt options the device offers after each
                      payment. Turn all off to skip the receipt screen and go
                      straight to the success screen.
                    </info-hint>
                    <toggle-field
                      v-model="page.receipt_email_on_file"
                      label="Email on file"
                      hint="Send to the customer email already on the transaction, shown masked on the device."
                      class="mb-3"
                    />
                    <toggle-field
                      v-model="page.receipt_custom_email"
                      label="Typed email"
                      hint="Customer types an address on the device."
                    />
                    <!-- receipt_sms exists in the schema but stays hidden here
                         until SMS dispatch is built (never offer a toggle the
                         device ignores). -->
                  </collapsible-section>
                </div>

                <!-- ═══ Appearance ═════════════════════════════════════ -->
                <div v-if="activeSection === 'appearance'" :key="'tab-appearance'">
                  <collapsible-section
                    title="Logo"
                    icon="tim-icons icon-image-02"
                    :badge="page.show_logo === 1 ? 'Shown' : ''"
                  >
                    <toggle-field v-model="page.show_logo" label="Show logo on this page" :class="page.show_logo === 1 ? 'mb-3' : 'mb-0'" />
                    <template v-if="page.show_logo === 1">
                      <div v-if="!page.logo_image_url">
                        <div class="ppe__logo-drop" @click="openLogoEditor">
                          <i class="tim-icons icon-cloud-upload-94 ppe__logo-icon"></i>
                          <p class="ppe__logo-drop-title">Upload a logo</p>
                          <p class="ppe__hint mb-0">PNG, JPG, or SVG. Drag to position after upload.</p>
                        </div>
                        <image-editor
                          ref="logoEditor"
                          size="sm"
                          text="Upload"
                          @saveImage="onLogoSave"
                          class="ppe__logo-editor-hidden"
                        />
                      </div>
                      <div v-else class="ppe__logo-preview">
                        <img
                          :src="page.logo_image_url"
                          :style="logoPreviewStyle"
                          alt="Logo"
                        />
                        <div class="ppe__logo-actions">
                          <image-editor
                            :edited_image="page.logo_image"
                            :edit="true"
                            @saveImage="onLogoSave"
                          />
                          <base-button link type="primary" size="sm" @click="removeLogo()">Remove</base-button>
                        </div>
                      </div>
                    </template>
                  </collapsible-section>

                  <collapsible-section title="Typography" icon="tim-icons icon-pencil" :default-open="false">
                    <info-hint v-if="page.type === 'pos'" variant="warning">
                      The OTC POS mobile app can only render fonts bundled
                      into the app. Selecting a font here is honored when
                      it matches one of the bundled families; any other
                      choice falls back to the device's system font. Color
                      palette and logo apply on the device immediately.
                    </info-hint>
                    <div class="ppe__row">
                      <div class="ppe__col">
                        <label class="text-white">Body font</label>
                        <base-input>
                          <el-select
                            v-model="page.font_family"
                            class="select-primary"
                            :style="{ 'font-family': page.font_family }"
                            placeholder="Select a font"
                            filterable
                          >
                            <el-option
                              v-for="f in orderedFonts"
                              :key="'body-' + f.family"
                              :style="{ 'font-family': f.family }"
                              class="select-primary"
                              :label="f.family"
                              :value="f.family"
                            />
                          </el-select>
                        </base-input>
                      </div>
                      <div class="ppe__col">
                        <label class="text-white">Card font</label>
                        <base-input>
                          <el-select
                            v-model="page.font_family_card"
                            class="select-primary"
                            :style="{ 'font-family': page.font_family_card }"
                            placeholder="Select a font"
                            filterable
                          >
                            <el-option
                              v-for="f in orderedFonts"
                              :key="'card-' + f.family"
                              :style="{ 'font-family': f.family }"
                              class="select-primary"
                              :label="f.family"
                              :value="f.family"
                            />
                          </el-select>
                        </base-input>
                      </div>
                    </div>
                  </collapsible-section>

                  <collapsible-section title="Color palette" icon="tim-icons icon-palette" :default-open="false">
                    <div class="ppe__palette">
                      <section class="ppe__palette-group">
                        <header class="ppe__palette-group-head">
                          <span class="ppe__palette-group-eyebrow">Page</span>
                        </header>
                        <div class="ppe__palette-group-fields">
                          <color-field v-model="page.background_color" label="Background" />
                          <color-field v-model="page.text_color" label="Text" />
                          <color-field v-model="page.link_color" label="Links" />
                        </div>
                      </section>

                      <section class="ppe__palette-group">
                        <header class="ppe__palette-group-head">
                          <span class="ppe__palette-group-eyebrow">Components</span>
                        </header>
                        <div class="ppe__palette-group-fields">
                          <color-field v-model="page.card_background_color" label="Card background" />
                          <color-field v-model="page.card_text_color" label="Card text" />
                          <color-field v-model="page.button_color" label="Button fill" />
                          <color-field v-model="page.button_text_color" label="Button label" />
                          <color-field v-model="page.input_border_color" label="Input border" />
                        </div>
                      </section>
                    </div>
                    <div class="ppe__palette-footer">
                      <a href="#" class="ppe__palette-reset" @click.prevent="resetColors">
                        <i class="tim-icons icon-refresh-01"></i>
                        Reset to defaults
                      </a>
                    </div>
                  </collapsible-section>
                </div>

                <!-- ═══ Payments ═══════════════════════════════════════ -->
                <div v-if="activeSection === 'payments'" :key="'tab-payments'">
                  <!-- Methods picker is link-only. POS pages always run through
                       Stripe Terminal card-present capture; there's no "pick
                       a method" step on the tablet, so configuring methods
                       would be meaningless and confusing. Schema column
                       stays so the section transparently re-enables if a
                       page later becomes a link page. -->
                  <collapsible-section
                    v-if="page.type !== 'pos'"
                    title="Methods on this page"
                    icon="tim-icons icon-credit-card"
                    :badge="linkedPaymentMethods.length ? String(linkedPaymentMethods.length) : ''"
                  >
                    <linked-item-list
                      :items="linkedPaymentMethods"
                      :addable="addablePaymentMethods"
                      reorderable
                      add-placeholder="Add method from catalog"
                      empty-icon="icon-credit-card"
                      empty-title="No methods linked"
                      empty-hint="Pick from your venue catalog to expose methods here. Drag to set the order customers see."
                      @add="addPaymentMethod"
                      @remove="removePaymentMethod"
                      @update="updatePaymentMethod"
                      @reorder="reorderPaymentMethods"
                    />
                    <hr class="ppe__inline-rule" />
                    <label class="text-white">Display style</label>
                    <base-input>
                      <el-select v-model="page.payment_layout_type" class="select-primary">
                        <el-option value="dropdown" label="Dropdown menu" class="select-primary" />
                        <el-option value="buttons" label="Button group" class="select-primary" />
                      </el-select>
                    </base-input>
                    <p class="ppe__hint mb-0">How methods are presented at checkout.</p>
                  </collapsible-section>

                  <collapsible-section
                    title="Tipping"
                    icon="tim-icons icon-money-coins"
                    :default-open="page.type === 'pos'"
                    :badge="page.allow_tipping === 1 ? 'On' : ''"
                  >
                    <!-- Master toggle. Everything below is subordinate to it. -->
                    <toggle-field v-model="page.allow_tipping" label="Allow tipping on this page" />

                    <div v-if="page.allow_tipping === 1" class="ppe__tip-body">
                      <div class="ppe__row">
                        <div class="ppe__col"><base-input label="Field label" v-model="page.tip_label" class="mb-0" /></div>
                        <div class="ppe__col"><base-input label="Line item label" v-model="page.tip_line_item_label" class="mb-0" /></div>
                      </div>

                      <!-- Calculation base — the binary is the control, so the
                           hint shrinks to one clarifying line. -->
                      <div class="ppe__tip-base">
                        <label class="ppe__label">Tip is based on</label>
                        <segmented-toggle
                          v-model="page.tip_on_full_amount"
                          variant="pills"
                          :options="[
                            { value: 0, label: 'Amount due' },
                            { value: 1, label: 'Whole order' },
                            { value: 2, label: 'Subtotal before taxes and fees' }
                          ]"
                        />
                        <info-hint variant="primary">
                          Amount due bases percentage tips on the remaining balance, taxes and fees included.
                          Whole order bases them on the full order total even when part is already paid.
                          Subtotal before taxes and fees bases them on the order subtotal after discounts.
                          Customers always pay the amount due plus the tip, and the device shows the
                          amount the tip is based on when it differs from the amount due.
                        </info-hint>
                      </div>

                      <!-- Preset options: its own card -->
                      <div class="ppe__tip-subgroup">
                        <toggle-field
                          v-model="page.allow_preset_tipping_options"
                          label="Preset options"
                          hint="Quick-pick chips on the device. The amount is the headline, your label sits beneath it."
                        />
                        <div v-if="page.allow_preset_tipping_options === 1" class="ppe__tip-presets">
                          <div v-if="tipPresets.length === 0" class="ppe__tip-presets-empty">
                            No presets yet. Add your first below.
                          </div>
                          <template v-else>
                            <div class="ppe__tip-preset-head">
                              <label class="ppe__label">Label</label>
                              <label class="ppe__label">Amount</label>
                            </div>
                            <ul class="ppe__tip-presets-list">
                              <li
                                v-for="(p, idx) in tipPresets"
                                :key="p.id || `new-${idx}`"
                                class="ppe__tip-preset-row"
                              >
                                <base-input
                                  v-model="p.name"
                                  class="mb-0"
                                  placeholder="Label (e.g. Good)"
                                  maxlength="50"
                                />
                                <base-input
                                  v-model.number="p.amount"
                                  class="mb-0"
                                  type="number"
                                  min="0"
                                  :step="p.value_type === 0 ? '1' : '0.01'"
                                  placeholder="0"
                                />
                                <segmented-toggle
                                  v-model="p.value_type"
                                  variant="pills"
                                  size="sm"
                                  :options="[{ value: 0, label: '%' }, { value: 1, label: '$' }]"
                                />
                                <button
                                  type="button"
                                  class="ppe__tip-preset-remove"
                                  aria-label="Remove preset"
                                  title="Remove"
                                  @click="removeTipPreset(idx)"
                                >
                                  <i class="tim-icons icon-simple-remove"></i>
                                </button>
                              </li>
                            </ul>
                          </template>
                          <base-button
                            type="primary"
                            size="sm"
                            class="mt-2"
                            @click="addTipPreset"
                          >
                            <i class="tim-icons icon-simple-add mr-1"></i>
                            Add preset
                          </base-button>
                        </div>
                      </div>

                      <!-- Custom amount: its own card -->
                      <div class="ppe__tip-subgroup">
                        <toggle-field
                          v-model="page.allow_custom_tipping_options"
                          label="Custom amount"
                          hint="Customer types their own tip on the device."
                        />
                        <div v-if="page.allow_custom_tipping_options === 1" class="ppe__tip-custom-type">
                          <label class="ppe__label">Custom amount type</label>
                          <segmented-toggle
                            v-model="page.custom_tipping_value_type"
                            variant="pills"
                            :options="[
                              { value: 0, label: 'Percentage of the tip base' },
                              { value: 1, label: 'Flat amount' }
                            ]"
                          />
                        </div>
                      </div>
                    </div>
                  </collapsible-section>

                  <!-- Hidden for POS pages (2026-06-11): promo-on-device
                       (task #65 phase 2) is parked — the device never got
                       the promo field, so offering the toggle here only
                       configured something the app ignores. Payment-link
                       pages keep it (the link checkout implements it).
                       show_promo_code_field + related columns stay; the
                       device payload still carries the flags. -->
                  <collapsible-section
                    v-if="page.type !== 'pos'"
                    title="Promo codes"
                    icon="tim-icons icon-tag"
                    :default-open="false"
                    :badge="page.show_promo_code_field === 1 ? 'On' : ''"
                  >
                    <toggle-field v-model="page.show_promo_code_field" label="Show promo code field at checkout" class="mb-3" />
                    <div v-if="page.show_promo_code_field === 1">
                      <div class="ppe__row">
                        <div class="ppe__col">
                          <base-input label="Placeholder" v-model="page.promo_code_placeholder_text" placeholder="Enter promo code" />
                        </div>
                        <div class="ppe__col">
                          <label class="text-white">Error handling</label>
                          <div class="ppe__toggle-stack">
                            <toggle-field v-model="page.show_custom_promo_code_error_message" label="Custom error message" />
                          </div>
                        </div>
                      </div>
                      <base-input
                        v-if="page.show_custom_promo_code_error_message === 1"
                        label="Error message"
                        v-model="page.promo_code_error_message"
                      />
                    </div>
                  </collapsible-section>

                  <collapsible-section
                    v-if="page.type === 'link'"
                    title="Expiration"
                    icon="tim-icons icon-time-alarm"
                    :default-open="false"
                    :badge="page.payment_request_has_timeout === 1 ? 'On' : ''"
                  >
                    <toggle-field
                      v-model="page.payment_request_has_timeout"
                      label="Expire this payment link after a period"
                      hint="The link cancels and the transaction is voided if the deadline passes without enough paid."
                      class="mb-3"
                    />
                    <div v-if="page.payment_request_has_timeout === 1">
                      <value-period-input
                        label="Expires after"
                        v-model="page.payment_request_timeout_value"
                        :period.sync="page.payment_request_timeout_period"
                        :periods="timeoutPeriods"
                        :min="1"
                        class="mb-3 ppe__period-input"
                      />
                      <percent-input
                        label="Required payment"
                        :value="page.payment_request_threshold"
                        :error="errs.payment_request_threshold"
                        @input="onThresholdInput"
                      />
                      <p class="ppe__hint">Cancel the link if less than this percentage of the total is paid by the deadline.</p>
                    </div>
                  </collapsible-section>

                  <collapsible-section
                    v-if="page.type === 'link'"
                    title="Custom amounts"
                    icon="tim-icons icon-coins"
                    :default-open="false"
                    :badge="page.payment_request_allow_custom_amounts === 1 ? 'On' : ''"
                  >
                    <toggle-field
                      v-model="page.payment_request_allow_custom_amounts"
                      label="Allow custom amounts"
                      hint="Customers can pay a different amount than what's owed (e.g. partial payments)."
                    />
                  </collapsible-section>
                </div>
              </div>
            </div>

            <!-- Footer: save / discard -->
            <div class="ppe__footer">
              <span class="ppe__footer-msg" :class="{ 'ppe__footer-msg--dirty': isDirty }">
                <span v-if="isDirty" class="ppe__footer-dot"></span>
                <span v-if="isDirty">Unsaved changes</span>
                <span v-else-if="isNew">Ready to create</span>
                <span v-else>All changes saved</span>
              </span>
              <div class="ppe__footer-actions">
                <!-- Cancel only when creating (abandon the new page). No
                     Discard on an existing page — reverting unsaved edits
                     isn't a pattern used elsewhere in the app, so it read
                     as confusing. The dirty-state indicator + Save cover it. -->
                <base-button
                  v-if="isNew"
                  simple
                  type="primary"
                  size="sm"
                  @click="cancel"
                >
                  Cancel
                </base-button>
                <base-button
                  type="primary"
                  size="sm"
                  @click="save"
                  :disabled="!canSave"
                >
                  <span v-if="!saving">{{ isNew ? 'Create page' : 'Save changes' }}</span>
                  <span v-else>Saving…</span>
                </base-button>
              </div>
            </div>
          </component>
        </div>

        <!-- ─── Preview column (sticky) ───────────────────────────────── -->
        <!-- HIDDEN for now: live preview was distracting during early
             editor polish and didn't reflect the customer-facing renderer
             closely enough. Bring back once the customer-app's render
             matches PaymentPagePreview's output 1:1 (or when we have a
             better embedded preview component). When restoring, also flip
             the editor column from col-lg-12 back to col-lg-9. -->
        <!--
        <div class="col-lg-3 d-none d-lg-block ppe__col-preview">
          <payment-page-preview
            :page="page"
            :linked-custom-fields="linkedCustomFields"
            :linked-payment-methods="linkedPaymentMethods"
          />
        </div>
        -->
        <!-- /HIDDEN preview -->
      </div>
    </div>
  </div>
</template>

<script>
import { Select, Option, Dropdown, DropdownMenu, Switch, Tooltip } from "element-ui";
import { Badge } from "src/components";
import QrcodeVue from "qrcode.vue";
import Breadcrumb from "@/components/Breadcrumb/Breadcrumb";
import BreadcrumbItem from "@/components/Breadcrumb/BreadcrumbItem";
import EditorSectionNav from "@/components/PaymentPages/EditorSectionNav";
import ColorField from "@/components/PaymentPages/ColorField";
import ToggleField from "@/components/PaymentPages/ToggleField";
import LinkedItemList from "@/components/PaymentPages/LinkedItemList";
import PaymentPagePreview from "@/components/PaymentPages/PaymentPagePreview";
import CollapsibleSection from "@/pages/Bookings/BookinSiteSettings/components/shared/CollapsibleSection";
import ImageEditor from "@/components/ImageEditor";
import axios from "axios";
import _ from "lodash";
import { Auth } from "aws-amplify";
import { API_LOCATION, POS_APP_DOWNLOAD_URL, POS_APP_S710_DOWNLOAD_URL } from "@/api-config";

// Strip the system-font fallback stack off a stored font-family value.
// Legacy rows persisted the full stack ("Poppins, system-ui, …"); we now
// store just the family. This converts either form into the family name
// alone so the editor dropdown matches an <el-option> value.
function primaryFamilyOf(stack) {
  if (!stack) return 'Poppins';
  const first = String(stack).split(',')[0]?.trim().replace(/^["']|["']$/g, '');
  return first || 'Poppins';
}

const DEFAULT_PAGE = {
  // POS-only beta: new pages are always 'pos'. When invoicing ships, the
  // surface-type selector returns and this default is chosen by the route.
  name: '', type: 'pos', is_default: 0, archived: 0,
  title: '', subtext: '', confirmation_message: '',
  logo_image_id: null, logo_image_url: null, logo_image: null, show_logo: 1,
  background_color: '#27293d', text_color: '#ffffff', link_color: '#e14eca',
  card_background_color: '#1e1e2f', card_text_color: '#ffffff',
  button_color: '#e14eca', button_text_color: '#ffffff',
  input_border_color: '#7d7d7d99',
  // Store only the user's chosen family. System-font fallbacks are applied
  // at render time (ThemedShell on the customer-app, PaymentPagePreview in
  // the editor), so we don't bake them into the saved value.
  font_family: 'Poppins',
  font_family_card: 'Poppins',
  allow_tipping: 0, allow_custom_tipping_options: 0, custom_tipping_value_type: 0,
  tip_on_full_amount: 0,
  allow_preset_tipping_options: 0, tip_label: 'Want to leave a tip?', tip_line_item_label: 'Tips',
  show_promo_code_field: 1, show_custom_promo_code_error_message: 0,
  promo_code_error_message: 'Promo code invalid', promo_code_placeholder_text: '',
  payment_layout_type: 'dropdown',
  payment_request_has_timeout: 0, payment_request_timeout_value: 7, payment_request_timeout_period: 1,
  payment_request_threshold: 100, payment_request_allow_custom_amounts: 1,
  signature_required: 0, show_line_items: 1,
  receipt_email_on_file: 1, receipt_custom_email: 1, receipt_sms: 0,
  created_at: null, updated_at: null,
};
// Card reader linkage is NOT a payment_pages column — it's a pos_devices
// row (device_type='stripe_terminal_reader') under the page. See the
// Card Readers subsection in the Devices block.

const COLOR_DEFAULTS = {
  background_color: '#27293d', text_color: '#ffffff', link_color: '#e14eca',
  card_background_color: '#1e1e2f', card_text_color: '#ffffff',
  button_color: '#e14eca', button_text_color: '#ffffff',
  input_border_color: '#7d7d7d99',
};

export default {
  name: 'PaymentPageEditor',
  props: {
    // Embedded inside a settings tab: no breadcrumb, header actions, Cancel,
    // or route navigation; the host owns the page and hears `saved`/`missing`.
    embedded: { type: Boolean, default: false },
    // Page id when embedded (route params are not used then).
    pageIdProp: { type: [Number, String], default: null },
  },
  components: {
    Breadcrumb, BreadcrumbItem,
    EditorSectionNav, ColorField, ToggleField, LinkedItemList, PaymentPagePreview,
    CollapsibleSection,
    ImageEditor,
    Badge,
    QrcodeVue,
    [Switch.name]: Switch,
    [Tooltip.name]: Tooltip,
    [Select.name]: Select,
    [Option.name]: Option,
    [Dropdown.name]: Dropdown,
    [DropdownMenu.name]: DropdownMenu,
  },
  data() {
    return {
      confirmDialog: { show: false, title: '', message: '', confirmText: 'Confirm', cancelText: 'Cancel', confirmVariant: 'primary', variant: 'warning', resolve: null },
      loading: false,
      saving: false,
      activeSection: this.embedded ? 'payments' : 'general',
      page: JSON.parse(JSON.stringify(DEFAULT_PAGE)),
      linkedCustomFields: [],
      linkedPaymentMethods: [],
      tipPresets: [],
      availableCustomFields: [],
      availablePaymentMethods: [],
      fonts: [],
      timeoutPeriods: [
        { id: 4, label: 'Minutes' },
        { id: 0, label: 'Hours' },
        { id: 1, label: 'Days' },
        { id: 2, label: 'Weeks' },
        { id: 3, label: 'Months' },
      ],
      errs: { name: '', payment_request_threshold: '' },
      pristine: '',
      // Devices loaded from GET /payment-pages/{id}/devices. Live updates
      // arrive via a WS subscription (see initDevicesWebSocket) so the list
      // reflects pair / online / offline / revoke events without polling.
      devices: [],
      devicesLoading: false,
      devicesWs: null,
      devicesWsReconnectTimer: null,
      devicesWsShouldReconnect: false,
      // Stripe Terminal readers registered to the venue, from
      // companies-list-pos-readers. Only the platform-control flag of that
      // response is used here (the Set up a payment device block); readers
      // themselves are picked in Send to POS, never linked to a page.
      readers: [],
      // 1 when the connected Stripe account is platform controlled, 0 otherwise, null until loaded.
      stripePlatformControlled: null,
      readersLoading: false,
      // Auto-lock dropdown options — keep in sync with the device app's
      // AdminHome IDLE_OPTIONS (1/5/15/never).
      autoLockOptions: [
        { value: 1, label: '1 min' },
        { value: 5, label: '5 min' },
        { value: 15, label: '15 min' },
        { value: 0, label: 'Never' },
      ],
      // Inline rename state — only one row can be in edit mode at a time.
      editingDeviceId: null,
      editingDeviceName: '',
      originalDeviceName: '',
      pairForm: {
        open: false,
        deviceName: '',
        code: '',
        expiresAt: 0,
        expiresIn: 0,
        intervalId: null,
        loading: false,
        error: '',
      },
    };
  },
  computed: {
    pageId() {
      if (this.pageIdProp != null && this.pageIdProp !== '') return Number(this.pageIdProp);
      const id = this.$route.params.id;
      return !id || id === 'new' ? null : Number(id);
    },
    isNew() { return this.pageId === null; },
    posAppDownloadUrl() { return POS_APP_DOWNLOAD_URL; },
    posS710AppDownloadUrl() { return POS_APP_S710_DOWNLOAD_URL; },
    typeLabel() {
      const map = { link: 'Payment request', pos: 'POS', invoice: 'Invoice' };
      return map[this.page.type] || this.page.type;
    },
    typeBadgeType() { return this.page.type === 'pos' ? 'primary' : 'success'; },
    typeIconClass() {
      const map = { link: 'icon-link-72', pos: 'icon-credit-card', invoice: 'icon-paper' };
      return map[this.page.type] || 'icon-tap-02';
    },
    headerTitle() {
      if (this.embedded) return 'Payment request page';
      if (this.isNew) return 'New POS page';
      return this.page.name || 'Untitled page';
    },
    sections() {
      if (this.embedded) {
        // Payment request template inside Payment settings: General would be
        // empty (Identity hidden, the other General sections are POS only).
        return [
          { id: 'payments',   label: 'Payments' },
          { id: 'content',    label: 'Content' },
          { id: 'appearance', label: 'Appearance' },
        ];
      }
      return [
        { id: 'general',    label: 'General',    warn: !this.page.name },
        { id: 'appearance', label: 'Appearance' },
        { id: 'content',    label: 'Content' },
        { id: 'payments',   label: 'Payments' },
      ];
    },
    orderedFonts() {
      const skip = new Set([
        'Libre Baskerville',
        'Material Icons',
        'Material Icons Outlined',
        'Material Icons Sharp',
        'Material Icons Round',
        'M PLUS Rounded 1c',
      ]);
      const filtered = (this.fonts || []).filter(f => !skip.has(f.family));
      const deduped = _.uniqBy(filtered, 'family');
      return _.orderBy(deduped, 'family');
    },
    logoPreviewStyle() {
      const img = this.page.logo_image;
      if (!img) return {};
      return {
        transform: `translate(0%, 0%) scale(${(img.scale || 100) * 0.01})`,
        left: (img.x || 0) + 'px',
        top: (img.y || 0) + 'px',
        objectFit: img.object_fit || 'cover',
      };
    },
    isDirty() {
      if (!this.pristine) return false;
      return this.currentSnapshot() !== this.pristine;
    },
    canSave() {
      if (this.saving) return false;
      if (!this.page.name || !this.page.name.trim()) return false;
      return this.isDirty || this.isNew;
    },
    addableCustomFields() {
      const linked = new Set(this.linkedCustomFields.map(f => f.custom_field_id));
      return this.availableCustomFields.filter(f => !linked.has(f.id));
    },
    customFieldToggles() {
      // Mirrors BSS ContentTab: "required" applies to every type (on a
      // checkbox it means the box must be checked); "requires user
      // interaction" is checkbox-only; alert applies to every type.
      return [
        {
          key: 'required',
          label: 'Required',
          tooltip: 'Make field required',
        },
        {
          key: 'interaction_required',
          label: 'Requires user interaction',
          appliesTo: (f) => Number(f.type) === 1,
        },
        {
          key: 'add_alert',
          label: 'Creates alert',
          tooltip: 'Show alert on the transaction when filled out',
        },
      ];
    },
    addablePaymentMethods() {
      const linked = new Set(this.linkedPaymentMethods.map(m => m.custom_payment_method_id));
      return this.availablePaymentMethods.filter(m => !linked.has(m.id));
    },
    formattedCode() {
      // Render as "483 921" — two groups of 3 for easier reading at a glance.
      const c = this.pairForm.code || '';
      return c.length === 6 ? `${c.slice(0, 3)} ${c.slice(3)}` : c;
    },
    formattedCountdown() {
      const total = Math.max(0, this.pairForm.expiresIn);
      const m = Math.floor(total / 60);
      const s = total % 60;
      return `${m}:${s.toString().padStart(2, '0')}`;
    },
  },
  beforeDestroy() {
    this.stopPairCountdown();
    this.closeDevicesWebSocket();
  },
  methods: {
    async getAuthToken() {
      const data = await Auth.currentSession();
      return data.idToken.jwtToken;
    },
    apiBaseUrl() {
      const companyId = localStorage.getItem('companyId');
      const companyGroupId = JSON.parse(localStorage.getItem('group')).id;
      return `${API_LOCATION}companies/${companyId}/groups/${companyGroupId}/payment-pages`;
    },
    stripeReadersUrl() {
      const companyId = localStorage.getItem('companyId');
      const companyGroupId = JSON.parse(localStorage.getItem('group')).id;
      return `${API_LOCATION}companies/${companyId}/groups/${companyGroupId}/stripe-readers`;
    },
    async fetchReaders() {
      // Readers are venue-wide (group-scoped URL), so they load on new pages
      // too — the load-existing-page path isn't the only place they're needed.
      if (this.page.type !== 'pos') return;
      this.readersLoading = true;
      try {
        const token = await this.getAuthToken();
        const res = await axios.get(this.stripeReadersUrl(), {
          headers: { Authorization: token },
        });
        this.readers = Array.isArray(res.data?.readers) ? res.data.readers : [];
        this.stripePlatformControlled = res.data?.stripe_platform_controlled === 1 ? 1 : 0;
      } catch (err) {
        // Quiet failure — show empty state. Most likely cause: Stripe
        // not yet configured or zero readers registered. Real errors
        // (5xx) still get logged.
        if (err.response && err.response.status >= 500) {
          console.error('Failed to load Stripe readers', err);
        }
        this.readers = [];
      } finally {
        this.readersLoading = false;
      }
    },
    onAddDeviceCommand() {
      // Single entry point for Add device. Only the OTC POS app pair form
      // exists; readers are registered under Payment settings and picked in
      // Send to POS.
      // New page: pairing binds to a saved page id. Rather than tell the user
      // to "create first", save the page transparently — the post-create flow
      // (autoOpenDevicesOnNextTick) then opens the pair form.
      // If the page has no name, save() surfaces the standard required-field
      // error on the Name field above.
      if (this.isNew) {
        this.save();
        return;
      }
      this.openPairForm();
    },
    async fetch() {
      if (this.isNew) {
        const t = this.$route.query.type;
        if (t === 'link' || t === 'pos') this.page.type = t;
        await this.loadCatalogs();
        this.snapshotPristine();
        return;
      }
      this.loading = true;
      try {
        const token = await this.getAuthToken();
        const url = `${this.apiBaseUrl()}/${this.pageId}`;
        const res = await axios.get(url, { headers: { Authorization: token } });
        const { payment_page, linked_methods, linked_custom_fields,
                tip_presets, available_methods, available_fields } = res.data;
        // Merge backend row into local page shape; backend returns nulls where
        // defaults make more sense visually.
        this.page = { ...JSON.parse(JSON.stringify(DEFAULT_PAGE)), ...payment_page };
        // Normalize legacy "Poppins, system-ui, …" stacks to just the
        // primary family. Storing the stack was the historical default;
        // we now store the family alone and append fallbacks at render time.
        this.page.font_family = primaryFamilyOf(this.page.font_family);
        this.page.font_family_card = primaryFamilyOf(this.page.font_family_card);
        // The tipping segmented-toggles match option values with strict ===,
        // so these flags must be numbers — coerce like the tip presets below
        // do, in case the backend ever serializes tinyints as strings.
        this.page.tip_on_full_amount = Number(this.page.tip_on_full_amount) || 0;
        this.page.custom_tipping_value_type = Number(this.page.custom_tipping_value_type) || 0;
        this.linkedCustomFields = linked_custom_fields.map((f) => ({
          id: f.id,
          custom_field_id: f.custom_field_id,
          name: f.label, type: f.type,
          position: f.position, active: f.active,
          required: f.required,
          interaction_required: f.interaction_required,
          add_alert: f.add_alert,
        }));
        this.linkedPaymentMethods = linked_methods.map((m) => ({
          id: m.id,
          custom_payment_method_id: m.custom_payment_method_id,
          name: m.name,
          position: m.position, active: m.active,
        }));
        this.availableCustomFields = available_fields.map((f) => ({
          id: f.id, name: f.label, type: f.type,
          // Library defaults — seed the link row's flags at add time,
          // same as BSS template links.
          required: f.required,
          interaction_required: f.interaction_required,
          add_alert: f.add_alert,
        }));
        this.availablePaymentMethods = available_methods;
        // Tip presets — same shape as the API row, kept as a flat array.
        // Editor mutates rows in place; save sends the array back wholesale.
        this.tipPresets = (tip_presets || []).map((p) => ({
          id: p.id,
          name: p.name,
          amount: Number(p.amount),
          value_type: Number(p.value_type),
        }));
        this.snapshotPristine();
        // POS templates: pull the paired devices list and open the WS
        // subscription that keeps it live (online/offline/revoked/paired
        // events fan out from the backend). Fire-and-forget, its loading
        // state is independent of the editor's main spinner.
        if (this.page.type === 'pos') {
          this.fetchDevices();
          this.fetchReaders();
          this.initDevicesWebSocket();
        }
      } catch (err) {
        console.error('Failed to load payment page', err);
        if (err.response?.status === 404) {
          this.$notify({ type: 'danger', message: 'Payment page not found' });
          if (this.embedded) this.$emit('missing');
          else this.$router.push('/purchases/point-of-sale');
        } else {
          this.$notify({ type: 'danger', message: 'Could not load payment page' });
        }
      } finally {
        this.loading = false;
      }
    },
    async loadCatalogs() {
      // For new-page creation. Fetch available methods + fields once. Reuses
      // the get-by-id endpoint with a synthetic id of 0 would be wasteful;
      // instead we accept the catalogs come back empty until first save and
      // refetch via the by-id endpoint after creation. For now: no-op stubs
      // so the editor renders. A dedicated GET /companies/.../groups/.../
      // payment-page-catalogs endpoint would be cleaner if this is painful.
      this.availableCustomFields = [];
      this.availablePaymentMethods = [];
    },
    currentSnapshot() {
      // Everything buildSavePayload() persists must be represented here, or
      // edits to it never light up Save (tip presets were missing — editing a
      // preset label/amount left isDirty false while the save payload would
      // have carried the change).
      return JSON.stringify({
        page: this.page,
        cf: this.linkedCustomFields,
        pm: this.linkedPaymentMethods,
        tp: this.tipPresets,
      });
    },
    snapshotPristine() { this.pristine = this.currentSnapshot(); },
    setSection(id) { this.activeSection = id; },
    addCustomField(id) {
      const cf = this.availableCustomFields.find(f => f.id === id); if (!cf) return;
      this.linkedCustomFields.push({
        id: `tmp-${Date.now()}`,
        custom_field_id: cf.id, name: cf.name, type: cf.type,
        position: this.linkedCustomFields.length, active: 1,
        required: cf.required ? 1 : 0,
        interaction_required: cf.interaction_required ? 1 : 0,
        add_alert: cf.add_alert ? 1 : 0,
      });
    },
    setCustomFieldFlag(idx, key, value) {
      this.$set(this.linkedCustomFields[idx], key, value);
    },
    addTipPreset() {
      // Default new presets to a percentage option since most venues
      // configure tip as percentages first.
      this.tipPresets.push({
        id: `tmp-${Date.now()}`,
        name: '',
        amount: null,
        value_type: 0,
      });
    },
    removeTipPreset(idx) {
      this.tipPresets.splice(idx, 1);
    },
    removeCustomField(idx) { this.linkedCustomFields.splice(idx, 1); },
    addPaymentMethod(id) {
      const m = this.availablePaymentMethods.find(x => x.id === id); if (!m) return;
      this.linkedPaymentMethods.push({
        id: `tmp-${Date.now()}`,
        custom_payment_method_id: m.id, name: m.name,
        position: this.linkedPaymentMethods.length, active: 1,
      });
    },
    onThresholdInput(val) {
      // Whole percent, 0 to 100. Empty is not a valid threshold: the cron
      // compares paid against threshold * total.
      if (val === null || val === undefined || Number.isNaN(Number(val))) {
        this.page.payment_request_threshold = null;
        this.errs.payment_request_threshold = 'Enter a percentage from 0 to 100';
        return;
      }
      const n = Math.round(Number(val));
      this.page.payment_request_threshold = Math.min(100, Math.max(0, n));
      this.errs.payment_request_threshold = '';
    },
    reorderPaymentMethods(list) {
      // Position is the array index at save time (buildSavePayload).
      this.linkedPaymentMethods = list;
    },
    removePaymentMethod(idx) { this.linkedPaymentMethods.splice(idx, 1); },
    updatePaymentMethod(idx, field, value) {
      this.$set(this.linkedPaymentMethods[idx], field, value);
    },
    // ─── Devices ──────────────────────────────────────────────────────
    deviceStatusLabel(status) {
      const map = { active: 'Active', offline: 'Offline', revoked: 'Revoked' };
      return map[status] || status;
    },
    displayStatus(device) {
      // The DB stores only 'active' or 'revoked'. Online/offline comes
      // from the lambda's is_online (live WS connection presence).
      // Fall back to last_seen_at freshness when is_online is missing
      // (older lambda version during a deploy window).
      if (device.status === 'revoked') return 'revoked';
      if (typeof device.is_online === 'boolean') {
        return device.is_online ? 'active' : 'offline';
      }
      const ONLINE_THRESHOLD_MS = 60 * 1000;
      if (!device.last_seen_at) return 'offline';
      const lastSeenMs = new Date(device.last_seen_at).getTime();
      if (isNaN(lastSeenMs)) return 'offline';
      return Date.now() - lastSeenMs < ONLINE_THRESHOLD_MS ? 'active' : 'offline';
    },
    async fetchDevices() {
      if (this.isNew) return;
      this.devicesLoading = true;
      try {
        const token = await this.getAuthToken();
        const url = `${this.apiBaseUrl()}/${this.pageId}/devices`;
        const res = await axios.get(url, { headers: { Authorization: token } });
        const prevIds = new Set((this.devices || []).map((d) => d.id));
        // App devices only. A legacy page-linked reader row (all revoked) must
        // never render as an app device.
        const next = (res.data.devices || []).filter((d) => d.device_type === 'otc_pos_tablet' || !d.device_type);
        this.devices = next;
        // If the pair form is open and a fresh device just appeared in
        // this poll, the tablet successfully redeemed the pair code. Close
        // the form and apply the admin's typed name (if any) via a rename.
        if (this.pairForm.open) {
          const newDevice = next.find((d) => !prevIds.has(d.id));
          if (newDevice) await this.onDevicePaired(newDevice);
        }
      } catch (err) {
        console.error('Failed to load POS devices', err);
        // Quiet failure — devices section just shows empty + admin can
        // reopen the editor to retry. Don't toast (the page itself is fine).
      } finally {
        this.devicesLoading = false;
      }
    },
    // Bridge between the WS pos_device:paired event and onDevicePaired.
    // The WS payload only carries device_id; we re-fetch the roster (which
    // includes the new row) and hand the full device record to the
    // existing handler so the typed-name rename + notify + form-close
    // logic runs unchanged. If the refetched list somehow doesn't
    // contain the new device (race during a sibling-page edit), fall
    // back to a minimal object so the rename still goes through.
    async handlePairedFromWs(deviceId) {
      // Dedupe: the same pos_device:paired event can arrive twice when
      // the FE is briefly subscribed via two connections (eg. during a
      // reconnect, or if multiple tabs of the editor are open). Without
      // a guard the rename+toast fires once per delivery.
      const now = Date.now();
      this._recentPairHandles = this._recentPairHandles || new Map();
      const last = this._recentPairHandles.get(deviceId);
      if (last && now - last < 3000) return;
      this._recentPairHandles.set(deviceId, now);

      await this.fetchDevices();
      const device =
        this.devices.find((d) => d.id === deviceId) ||
        { id: deviceId, name: 'POS device' };
      await this.onDevicePaired(device);
    },
    async onDevicePaired(device) {
      // Read the typed name BEFORE cancelPairForm clears it.
      const desiredName = (this.pairForm.deviceName || '').trim();
      if (desiredName && desiredName !== device.name) {
        try {
          const token = await this.getAuthToken();
          const url = `${this.apiBaseUrl()}/${this.pageId}/devices/${device.id}`;
          await axios.put(url, { name: desiredName }, { headers: { Authorization: token } });
          // Optimistically reflect the rename in the local list so the
          // row updates without waiting for the next poll.
          const idx = this.devices.findIndex((d) => d.id === device.id);
          if (idx !== -1) this.$set(this.devices[idx], 'name', desiredName);
        } catch (err) {
          console.error('Rename on pair failed', err);
          this.$notify({
            type: 'warning',
            message: 'Device paired, but the name you entered didn\'t save. You can edit it inline.',
          });
        }
      }
      this.$notify({
        type: 'success',
        message: `${desiredName || device.name} paired`,
      });
      this.cancelPairForm();
    },
    // WS subscription on this group's POS-devices roster. Backend lambdas
    // (ws-store-connection / ws-remove-connections / companies-revoke-pos-device
    // / pos-pair-device) push pos_device:online/offline/revoked/paired
    // events; we apply them to the local list, no polling. Reconnect with
    // a short backoff if the socket drops mid-session.
    initDevicesWebSocket() {
      console.log('[devicesWs] initDevicesWebSocket called', {
        pageType: this.page.type,
        isNew: this.isNew,
        pageId: this.pageId,
      });
      if (this.page.type !== 'pos' || this.isNew) {
        console.log('[devicesWs] bailing: not a saved POS page');
        return;
      }
      // Active group is stored as a JSON blob under 'group' (the
      // 'companyGroupId' key doesn't exist in this codebase), so the
      // previous straight-getItem call always read null and the socket
      // never opened — hence the missing live pair/online refreshes.
      let companyGroupId;
      try {
        companyGroupId = Number(JSON.parse(localStorage.getItem('group'))?.id);
      } catch {
        companyGroupId = NaN;
      }
      if (!Number.isFinite(companyGroupId) || companyGroupId <= 0) {
        console.warn('[devicesWs] bailing: no companyGroupId in localStorage.group');
        return;
      }
      this.closeDevicesWebSocket();
      this.devicesWsShouldReconnect = true;
      const wsBase = 'wss://3bqa7hkquf.execute-api.us-east-1.amazonaws.com/production';
      const url = `${wsBase}?type=pos_devices_for_group&companyGroupId=${companyGroupId}`;
      console.log('[devicesWs] opening', url);
      try {
        const sock = new WebSocket(url);
        this.devicesWs = sock;
        sock.onopen = () => {
          console.log('[devicesWs] OPEN — group', companyGroupId);
          // Re-fetch on (re)connect so anything that changed while we
          // were disconnected is picked up before the next event arrives.
          this.fetchDevices();
        };
        sock.onmessage = (ev) => this.onDevicesWsMessage(ev);
        sock.onerror = (err) => console.warn('[devicesWs] error', err);
        sock.onclose = () => {
          this.devicesWs = null;
          if (this.devicesWsShouldReconnect) {
            this.devicesWsReconnectTimer = setTimeout(
              () => this.initDevicesWebSocket(),
              2000,
            );
          }
        };
      } catch (err) {
        console.warn('[devicesWs] open failed', err);
      }
    },
    closeDevicesWebSocket() {
      this.devicesWsShouldReconnect = false;
      if (this.devicesWsReconnectTimer) {
        clearTimeout(this.devicesWsReconnectTimer);
        this.devicesWsReconnectTimer = null;
      }
      if (this.devicesWs) {
        try { this.devicesWs.close(1000, 'page leaving'); } catch { /* ignore */ }
        this.devicesWs = null;
      }
    },
    onDevicesWsMessage(ev) {
      console.log('[devicesWs] MESSAGE', ev.data);
      let msg;
      try { msg = JSON.parse(ev.data); } catch { return; }
      if (!msg || typeof msg.type !== 'string') return;
      const deviceId = Number(msg.device_id);
      if (!Number.isFinite(deviceId)) return;

      switch (msg.type) {
        case 'pos_device:online': {
          const idx = this.devices.findIndex((d) => d.id === deviceId);
          if (idx !== -1) {
            this.$set(this.devices[idx], 'is_online', 1);
            this.$set(this.devices[idx], 'last_seen_at', new Date().toISOString());
          } else {
            // Online for a device we don't know about yet — refetch.
            this.fetchDevices();
          }
          break;
        }
        case 'pos_device:offline': {
          const idx = this.devices.findIndex((d) => d.id === deviceId);
          if (idx !== -1) this.$set(this.devices[idx], 'is_online', 0);
          break;
        }
        case 'pos_device:revoked': {
          this.devices = this.devices.filter((d) => d.id !== deviceId);
          break;
        }
        case 'pos_device:updated': {
          // Device-initiated settings change (e.g. auto-lock from the
          // tablet's admin screen). Payload is IDs-only on this channel;
          // silent refetch picks up the new values.
          this.fetchDevices();
          break;
        }
        case 'pos_device:paired': {
          // Only act if the new device is bound to THIS payment page;
          // others belong to sibling pages in the same group.
          if (Number(msg.payment_page_id) === this.pageId) {
            // Route through onDevicePaired so any name the operator
            // typed in the pair form gets applied (lambda creates the
            // device with a default name; rename happens client-side).
            // onDevicePaired itself calls cancelPairForm + notifies.
            this.handlePairedFromWs(deviceId);
          }
          break;
        }
        default:
          break;
      }
    },
    lastSeenLabel(iso) {
      const then = new Date(iso).getTime();
      if (isNaN(then)) return 'unknown';
      const diffSec = Math.max(0, Math.round((Date.now() - then) / 1000));
      if (diffSec < 60) return 'just now';
      const min = Math.round(diffSec / 60);
      if (min < 60) return `${min} min ago`;
      const hr = Math.round(min / 60);
      if (hr < 24) return `${hr} hr ago`;
      return `${Math.round(hr / 24)} d ago`;
    },
    openPairForm() {
      this.pairForm.open = true;
      this.pairForm.deviceName = '';
      this.pairForm.code = '';
      this.pairForm.error = '';
      this.requestNewPairCode();
      // Freshly-paired tablets arrive via the pos_device:paired WS event
      // (see initDevicesWebSocket); no polling needed.
    },
    cancelPairForm() {
      this.pairForm.open = false;
      this.stopPairCountdown();
      this.pairForm.code = '';
      this.pairForm.expiresAt = 0;
      this.pairForm.expiresIn = 0;
      this.pairForm.deviceName = '';
      this.pairForm.error = '';
    },
    regeneratePairCode() {
      this.requestNewPairCode();
    },
    async requestNewPairCode() {
      // POST /companies/{id}/groups/{grid}/payment-pages/{ppid}/pair-codes
      // → returns { code, expires_at } (plaintext code only here, never again).
      this.pairForm.loading = true;
      this.pairForm.error = '';
      this.stopPairCountdown();
      try {
        const token = await this.getAuthToken();
        const url = `${this.apiBaseUrl()}/${this.pageId}/pair-codes`;
        const res = await axios.post(
          url,
          { device_name: (this.pairForm.deviceName || '').trim() },
          { headers: { Authorization: token } },
        );
        this.pairForm.code = res.data.code;
        const expiresAtMs = new Date(res.data.expires_at).getTime();
        this.pairForm.expiresAt = expiresAtMs;
        this.pairForm.expiresIn = Math.max(0, Math.round((expiresAtMs - Date.now()) / 1000));
        this.pairForm.intervalId = setInterval(() => {
          const remaining = Math.max(
            0,
            Math.round((this.pairForm.expiresAt - Date.now()) / 1000),
          );
          this.pairForm.expiresIn = remaining;
          if (remaining <= 0) this.stopPairCountdown();
        }, 1000);
      } catch (err) {
        console.error('Pair code request failed', err);
        let msg = 'Could not generate a pairing code';
        try {
          const body = err.response?.data;
          const parsed = typeof body === 'string' ? JSON.parse(body) : body;
          if (parsed?.error) msg = parsed.error;
        } catch (_) {
          // fall through with default message
        }
        this.pairForm.error = msg;
        this.pairForm.code = '';
      } finally {
        this.pairForm.loading = false;
      }
    },
    stopPairCountdown() {
      if (this.pairForm.intervalId) {
        clearInterval(this.pairForm.intervalId);
        this.pairForm.intervalId = null;
      }
    },
    async revokeDevice(device) {
      if (!device) return;
      const confirmed = await this.askConfirm({
        title: 'Revoke this device?',
        message: `${device.name} will be signed out immediately. This can't be undone.`,
        confirmText: 'Revoke',
        confirmVariant: 'danger',
        variant: 'danger',
      });
      if (!confirmed) return;
      try {
        const token = await this.getAuthToken();
        const url = `${this.apiBaseUrl()}/${this.pageId}/devices/${device.id}/revoke`;
        await axios.put(url, {}, { headers: { Authorization: token } });
        this.$notify({ type: 'success', message: 'Device revoked' });
        await this.fetchDevices();
      } catch (err) {
        console.error('Revoke failed', err);
        const body = err.response?.data;
        const parsed = typeof body === 'string' ? body : body?.error;
        this.$notify({ type: 'danger', message: parsed || 'Could not revoke device' });
      }
    },
    startEditDeviceName(device) {
      this.editingDeviceId = device.id;
      this.editingDeviceName = device.name || '';
      this.originalDeviceName = device.name || '';
      this.$nextTick(() => {
        const ref = this.$refs[`deviceNameInput-${device.id}`];
        const el = Array.isArray(ref) ? ref[0] : ref;
        if (el) {
          el.focus();
          el.select();
        }
      });
    },
    cancelEditDeviceName() {
      this.editingDeviceName = this.originalDeviceName;
      this.editingDeviceId = null;
    },
    autoLockLabel(minutes) {
      const m = Number(minutes);
      if (m === 0) return 'never';
      if (!Number.isFinite(m) || m < 0) return '5 min';
      return `${m} min`;
    },
    async saveDeviceAutoLock(device, minutes) {
      if (Number(device.auto_lock_timeout_minutes) === Number(minutes)) return;
      const idx = this.devices.findIndex((d) => d.id === device.id);
      if (idx === -1) return;
      const original = device.auto_lock_timeout_minutes;
      // Optimistic update + roll back on failure (same pattern as rename).
      this.$set(this.devices[idx], 'auto_lock_timeout_minutes', minutes);
      try {
        const token = await this.getAuthToken();
        const url = `${this.apiBaseUrl()}/${this.pageId}/devices/${device.id}`;
        await axios.put(url, { auto_lock_timeout_minutes: minutes }, { headers: { Authorization: token } });
        this.$notify({ type: 'success', message: 'Auto-lock updated' });
      } catch (err) {
        console.error('Auto-lock update failed', err);
        this.$set(this.devices[idx], 'auto_lock_timeout_minutes', original);
        const body = err.response?.data;
        const parsed = typeof body === 'string' ? body : body?.error;
        this.$notify({ type: 'danger', message: parsed || 'Could not update auto-lock' });
      }
    },
    async saveDeviceName(device) {
      if (this.editingDeviceId !== device.id) return;
      const trimmed = (this.editingDeviceName || '').trim();
      const original = this.originalDeviceName;
      this.editingDeviceId = null;
      // No change → quiet exit. Empty → revert without saving (matches
      // ProjectTicket's title pattern).
      if (!trimmed) return;
      if (trimmed === original) return;
      // Optimistic update + roll back on failure.
      const idx = this.devices.findIndex((d) => d.id === device.id);
      if (idx === -1) return;
      this.$set(this.devices[idx], 'name', trimmed);
      try {
        const token = await this.getAuthToken();
        const url = `${this.apiBaseUrl()}/${this.pageId}/devices/${device.id}`;
        await axios.put(url, { name: trimmed }, { headers: { Authorization: token } });
        this.$notify({ type: 'success', message: 'Device renamed' });
        this.fetchDevices();
      } catch (err) {
        console.error('Rename failed', err);
        this.$set(this.devices[idx], 'name', original);
        const body = err.response?.data;
        const parsed = typeof body === 'string' ? body : body?.error;
        this.$notify({ type: 'danger', message: parsed || 'Could not rename device' });
      }
    },
    autoOpenDevicesOnNextTick() {
      // Called right after creating a new POS template. Need two ticks:
      // (1) Vue re-renders with the new pageId so the Devices section
      //     starts existing in the DOM, then (2) we can grab the ref,
      //     expand it, scroll it into view, and prime a pair code so the
      //     user can pair their first tablet without hunting for the UI.
      this.$nextTick(() => {
        this.$nextTick(() => {
          const section = this.$refs.devicesSection;
          if (!section) return;
          if (typeof section.open === 'function') section.open();
          // Open the pair form (the only device form; a plain "Create page"
          // lands here too).
          this.openPairForm();
          if (section.$el && typeof section.$el.scrollIntoView === 'function') {
            section.$el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        });
      });
    },
    resetColors() { Object.assign(this.page, COLOR_DEFAULTS); },
    openLogoEditor() {
      if (this.$refs.logoEditor) {
        this.$refs.logoEditor.uploadNew();
      }
    },
    async onLogoSave(image) {
      const token = await this.getAuthToken();
      const companyId = localStorage.getItem('companyId');
      const companyGroupId = JSON.parse(localStorage.getItem('group')).id;
      const imagesUrl = `${API_LOCATION}companies/${companyId}/groups/${companyGroupId}/images`;
      const body = {
        url: image.url,
        scale: image.scale,
        description: image.description,
        background_color: image.background_color,
        x: image.x,
        y: image.y,
        is_primary: 0,
        object_fit: image.object_fit,
      };
      try {
        let imageId;
        if (image.image_id) {
          await axios.put(`${imagesUrl}/${image.image_id}`, body, {
            headers: { Authorization: token },
          });
          imageId = image.image_id;
        } else {
          const res = await axios.post(imagesUrl, body, {
            headers: { Authorization: token },
          });
          imageId = Number(res.data);
        }
        this.page.logo_image_id = imageId;
        this.page.logo_image_url = image.url;
        this.page.logo_image = {
          id: imageId,
          url: image.url,
          scale: image.scale,
          background_color: image.background_color,
          description: image.description,
          x: image.x,
          y: image.y,
          object_fit: image.object_fit,
        };
      } catch (err) {
        console.error('Logo save failed', err);
        this.$notify({ type: 'danger', message: 'Could not save logo' });
      }
    },
    removeLogo() {
      this.page.logo_image_id = null;
      this.page.logo_image_url = null;
      this.page.logo_image = null;
    },
    buildSavePayload() {
      // Strips client-only / derived fields that aren't on payment_pages.
      const {
        logo_image_url, logo_image, // derived from images JOIN; never sent
        logo_image_scale, logo_image_object_fit, logo_image_x,
        logo_image_y, logo_image_background_color,
        id, company_group_id, created_at, updated_at, created_by, updated_by,
        ...persistable
      } = this.page;
      return {
        ...persistable,
        linked_methods: this.linkedPaymentMethods.map((m, i) => ({
          custom_payment_method_id: m.custom_payment_method_id,
          position: i,
          active: m.active,
        })),
        linked_custom_fields: this.linkedCustomFields.map((f, i) => ({
          custom_field_id: f.custom_field_id,
          position: i,
          active: f.active,
          required: f.required,
        })),
        // Tip presets — array order is preserved on the wire but the table
        // currently sorts by id, so reordering isn't user-facing yet.
        // Filter out empty/invalid rows so the lambda's validator doesn't
        // bounce the request.
        tip_presets: (this.tipPresets || [])
          .filter((p) => p.name && p.name.trim() && Number(p.amount) > 0)
          .map((p) => ({
            name: p.name.trim(),
            amount: Number(p.amount),
            value_type: Number(p.value_type) === 1 ? 1 : 0,
          })),
      };
    },
    async save() {
      this.errs.name = this.page.name.trim() ? '' : 'Name is required';
      if (this.errs.name) return;
      if (this.page.payment_request_has_timeout === 1
          && (this.page.payment_request_threshold === null || this.page.payment_request_threshold === undefined)) {
        this.errs.payment_request_threshold = 'Enter a percentage from 0 to 100';
        this.$notify({ type: 'danger', message: 'Required payment needs a percentage from 0 to 100' });
        return;
      }
      // Tip presets: buildSavePayload drops rows without a name or a positive
      // amount. Silently losing a half-filled row reads as data loss, so block
      // the save and say what's missing. Fully blank rows are treated as
      // abandoned and still drop quietly.
      if (this.page.allow_tipping === 1 && this.page.allow_preset_tipping_options === 1) {
        const partial = (this.tipPresets || []).find((p) => {
          const hasName = !!(p.name && p.name.trim());
          const hasAmount = Number(p.amount) > 0;
          return hasName !== hasAmount;
        });
        if (partial) {
          const label = partial.name && partial.name.trim();
          this.$notify({
            type: 'danger',
            message: label
              ? `Tip preset "${label}" needs an amount above 0`
              : 'Each tip preset needs a label',
          });
          return;
        }
      }
      this.saving = true;
      try {
        const token = await this.getAuthToken();
        const payload = this.buildSavePayload();
        if (this.isNew) {
          const wasPosCreate = this.page.type === 'pos';
          const res = await axios.post(this.apiBaseUrl(), payload, {
            headers: { Authorization: token },
          });
          const newId = res.data.id;
          this.$notify({ type: 'success', message: 'Page created' });
          // Redirect to canonical /:id URL so refresh works, then RE-FETCH:
          // the replace reuses this component (same route record), so
          // mounted() never re-runs — without the fetch the pristine snapshot
          // stays pre-save ("Unsaved changes" right after a successful create
          // + a false route-leave prompt) and this.page never gets its
          // id/badges/timestamps. fetch() hydrates and re-snapshots pristine.
          // Failure here is non-fatal (the page IS created) — fall back to
          // snapshotting the current form so the editor isn't spuriously
          // dirty, and don't let it trip the "Could not save" catch below.
          try {
            if (!this.embedded) await this.$router.replace(`/purchases/point-of-sale/${newId}`);
            await this.fetch();
          } catch (postCreateErr) {
            console.error('Post-create refresh failed', postCreateErr);
          }
          // fetch() catches its own errors without rethrowing — if it failed
          // to hydrate (no id on the page object), snapshot the local form so
          // the editor isn't spuriously dirty after a successful create.
          if (!this.page.id) this.snapshotPristine();
          // POS templates need a tablet to be useful — guide the user there
          // immediately rather than making them hunt for the Devices section.
          // Sequenced AFTER the fetch so the loading v-if has re-mounted the
          // sections and the devicesSection ref exists.
          if (wasPosCreate) this.autoOpenDevicesOnNextTick();
        } else {
          await axios.put(`${this.apiBaseUrl()}/${this.pageId}`, payload, {
            headers: { Authorization: token },
          });
          this.snapshotPristine();
          this.$notify({ type: 'success', message: 'Saved' });
          this.$emit('saved');
        }
      } catch (err) {
        console.error('Save failed', err);
        const msg = err.response?.data?.error || 'Could not save payment page';
        this.$notify({ type: 'danger', message: msg });
      } finally {
        this.saving = false;
      }
    },
    cancel() { if (!this.embedded) this.$router.push({ path: '/purchases/point-of-sale' }); },
    // Shared confirm dialog (base-alert-dialog). Resolves true on confirm.
    askConfirm({ title, message, confirmText = 'Confirm', cancelText = 'Cancel', confirmVariant = 'primary', variant = 'warning' }) {
      return new Promise((resolve) => {
        this.confirmDialog = { show: true, title, message, confirmText, cancelText, confirmVariant, variant, resolve };
      });
    },
    onConfirmDialogConfirm() {
      const resolve = this.confirmDialog.resolve;
      this.confirmDialog.show = false;
      if (resolve) resolve(true);
    },
    onConfirmDialogCancel() {
      const resolve = this.confirmDialog.resolve;
      this.confirmDialog.show = false;
      if (resolve) resolve(false);
    },
    async handleAction(command) {
      if (this.isNew) return;
      const token = await this.getAuthToken();
      const base = `${this.apiBaseUrl()}/${this.pageId}`;
      try {
        switch (command) {
          case 'setDefault': {
            await axios.put(`${base}/default`, {}, {
              headers: { Authorization: token },
            });
            this.page.is_default = 1;
            this.$notify({ type: 'success', message: 'Set as main' });
            break;
          }
          case 'duplicate': {
            const res = await axios.post(`${base}/duplicate`, {}, {
              headers: { Authorization: token },
            });
            this.$notify({ type: 'success', message: 'Duplicated' });
            this.$router.push(`/purchases/point-of-sale/${res.data.id}`);
            break;
          }
          case 'archive': {
            await axios.put(base, { archived: 1 }, {
              headers: { Authorization: token },
            });
            this.page.archived = 1;
            this.$notify({ type: 'success', message: 'Archived' });
            break;
          }
          case 'restore': {
            await axios.put(base, { archived: 0 }, {
              headers: { Authorization: token },
            });
            this.page.archived = 0;
            this.$notify({ type: 'success', message: 'Restored' });
            break;
          }
        }
      } catch (err) {
        console.error(`${command} failed`, err);
        const msg = err.response?.data?.error || `Could not ${command}`;
        this.$notify({ type: 'danger', message: msg });
      }
    },
    relative(iso) {
      if (!iso) return '';
      const d = new Date(iso);
      const diff = Date.now() - d.getTime();
      const m = Math.floor(diff / 60000);
      const h = Math.floor(diff / 3600000);
      const day = Math.floor(diff / 86400000);
      if (m < 1) return 'just now';
      if (m < 60) return `${m}m ago`;
      if (h < 24) return `${h}h ago`;
      if (day < 7) return `${day}d ago`;
      if (day < 30) return `${Math.floor(day / 7)}w ago`;
      return d.toLocaleDateString();
    },
    async getFonts() {
      try {
        const res = await axios.get(API_LOCATION + 'fonts');
        this.fonts = res.data || [];
      } catch (e) {
        console.error('Failed to load fonts', e);
      }
    },
  },
  mounted() {
    this.fetch();
    this.getFonts();
    // New pages skip fetch()'s load path (which fetches venue-wide Stripe
    // readers for saved pages), so fetch them here too.
    if (this.isNew) this.fetchReaders();
  },
  async beforeRouteLeave(to, from, next) {
    // Embedded in a settings tab, the host page owns navigation.
    if (this.embedded || !this.isDirty) {
      return next();
    }
    const confirmed = await this.askConfirm({
      title: 'Unsaved changes',
      message: 'You have unsaved changes on this page. Leave without saving?',
      confirmText: 'Leave',
      cancelText: 'Stay',
      confirmVariant: 'danger',
    });
    if (confirmed) {
      next();
    } else {
      next(false);
    }
  },
};
</script>

<style lang="scss" scoped>
.ppe {
  // Tighten info-hint's top spacing so it hugs the field/heading above
  // it instead of introducing visual section breaks. Scoped to the
  // editor only — other pages keep the global default (12px top/bottom).
  ::v-deep .info-hint {
    margin-top: 4px;
  }

  // When a hint LEADS a section body, pull it up against the header — the
  // body's 20px padding plus any hint margin reads as a dead gap there
  // (applies uniformly to every section in this editor that opens with a
  // hint: tipping, device setup, receipt screen).
  ::v-deep .collapsible-section__body > .info-hint:first-child {
    margin-top: -8px;
  }



  // ─── Payment device setup (phone + S710 options) ─────────────────────
  &__app-device {
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 1px solid rgba(125, 125, 125, 0.2);

    // Explicit modifier — :first-of-type never matched (the info-hint above
    // is also a div), which silently kept the divider + 2rem of spacing on
    // the first block. The app-section flex gap provides the spacing.
    &--first {
      margin-top: 0;
      padding-top: 0;
      border-top: none;
    }
  }

  &__app-device-title {
    font-size: 0.8rem;
    font-weight: 600;
    margin-bottom: 0.4rem;
    color: #e14eca;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  &__app-device-text {
    font-size: 0.85rem;
    margin-bottom: 0.75rem;
    max-width: 720px;
  }

  // Numbered setup steps — pink counters so the sequence scans at a glance.
  &__app-steps {
    font-size: 0.85rem;
    list-style: none;
    counter-reset: app-step;
    padding-left: 0;
    margin-bottom: 0.75rem;
    max-width: 720px;

    li {
      counter-increment: app-step;
      position: relative;
      padding-left: 1.9rem;
      margin-bottom: 0.5rem;

      &::before {
        content: counter(app-step);
        position: absolute;
        left: 0;
        top: 0.05rem;
        width: 1.25rem;
        height: 1.25rem;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        background: rgba(225, 78, 202, 0.15);
        color: #e14eca;
        font-size: 0.7rem;
        font-weight: 600;
      }
    }
  }

  // Stacked download cluster: QR tile, caption, button — left-aligned column
  // so the tile and button share the content's left edge.
  &__app-download {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    width: fit-content;
    gap: 6px;
  }

  // White tile behind the QR — keeps it scannable in dark mode and doubles
  // as the quiet zone the camera needs.
  &__app-qr-tile {
    padding: 8px;
    background: #fff;
    border-radius: 8px;
    line-height: 0;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.25);
  }

  &__app-qr-caption {
    white-space: nowrap;
    font-size: 0.75rem;
    margin-bottom: 4px;
    color: rgba(255, 255, 255, 0.65);

    .white-content & {
      color: rgba(0, 0, 0, 0.55);
    }
  }

  // Inside the Payment settings tab: no card of its own, so the header and
  // body get the padding the card used to provide.
  &__editor--embedded {
    padding: 4px 0 0;
  }

  // Labels get a touch of top-margin so they read as "starting a new
  // field" rather than glued to the info-hint above them.
  // Spacing only — font/color comes from the theme's bare <label> rule
  // so these match base-input labels exactly.
  &__label {
    display: block;
    margin-top: 12px;
    margin-bottom: 4px;
  }

  // Stack of toggles sharing one label — same vertical rhythm as the
  // input that lives in the sibling column, so the row reads as paired
  // controls instead of orphan toggles.
  &__toggle-stack {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding-top: 6px;
  }

  // Textarea styled to match the base-input look used elsewhere — dark
  // surface, subtle pink border, pink ring on focus. Scoped locally as
  // `.ppe__textarea` rather than lifted to global `.form-control` so we
  // don't ripple changes across every textarea in the app.
  &__textarea {
    display: block;
    width: 100%;
    padding: 10px 12px;
    background: rgba(30, 30, 47, 0.4);
    color: #ffffff;
    border: 1px solid rgba(225, 78, 202, 0.4);
    border-radius: 6px;
    font-size: 13px;
    font-family: inherit;
    line-height: 1.5;
    resize: vertical;
    outline: none;
    transition: border-color 0.12s ease, box-shadow 0.12s ease;
    margin-bottom: 12px;

    &::placeholder {
      color: rgba(255, 255, 255, 0.4);
    }

    &:hover { border-color: rgba(225, 78, 202, 0.7); }

    &:focus {
      border-color: #e14eca;
      box-shadow: 0 0 0 2px rgba(225, 78, 202, 0.2);
    }
  }

  // ─── Surface type read-only ─────────────────────────────────────────
  // Plain text + icon. Locked-after-creation is implicit; no need to
  // dress it as a disabled input or surface a lock badge.
  &__type-readonly {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 6px 0 0;
    padding: 0;
    font-size: 16px;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.95);
  }
  &__type-readonly-icon {
    font-size: 15px;
    color: #e14eca;
    flex-shrink: 0;
  }

  // ─── Local override for value-period-input width ────────────────────
  &__period-input {
    max-width: 260px;
  }

  // ─── Inline divider between groups inside one section ────────────────
  &__inline-rule {
    border: 0;
    border-top: 1px solid rgba(125, 125, 125, 0.12);
    margin: 20px 0 16px;
  }

  // ─── Inline toggle (switch + label, no hint block) ───────────────────
  &__inline-toggle {
    display: inline-flex;
    align-items: center;
    gap: 10px;

    label {
      font-size: 13px;
      line-height: 1;
    }
  }

  // ─── Devices section ────────────────────────────────────────────────
  &__device-type-chip {
    display: inline-block;
    padding: 1px 8px;
    border-radius: 10px;
    background: rgba(125, 125, 125, 0.14);
    color: rgba(255, 255, 255, 0.75);
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    margin-right: 4px;
  }

  &__readers-skel {
    margin: 4px 0 12px;
  }

  &__readers-picker {
    margin-bottom: 8px;

    ::v-deep .el-select {
      width: 100%;
    }
  }

  &__pair-form-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 12px;
  }

  &__device-meta-id {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 10.5px;
    color: rgba(255, 255, 255, 0.55);
    background: rgba(125, 125, 125, 0.08);
    padding: 1px 6px;
    border-radius: 3px;
  }


  &__devices-empty {
    text-align: center;
    padding: 24px 16px;
    background: rgba(125, 125, 125, 0.04);
    border: 1px dashed rgba(125, 125, 125, 0.3);
    border-radius: 6px;
    margin-bottom: 12px;
  }

  &__devices-empty-icon {
    display: inline-block;
    font-size: 28px;
    color: rgba(255, 255, 255, 0.45);
    margin-bottom: 6px;
  }

  &__devices-empty-title {
    margin: 4px 0 2px;
    font-size: 13.5px;
    color: rgba(255, 255, 255, 0.95);
  }

  &__devices-empty-hint {
    margin: 0;
    font-size: 11.5px;
    color: rgba(255, 255, 255, 0.6);
    max-width: 44ch;
    margin-left: auto;
    margin-right: auto;
    line-height: 1.5;
  }

  &__devices-list {
    list-style: none;
    padding: 0;
    margin: 0 0 12px;
  }

  &__app-section {
    display: flex;
    flex-direction: column;
    gap: 14px;

    // The flex gap is the single source of vertical rhythm here — strip the
    // info-hint's own 12px/4px margins so the space above and below it is
    // the same 14px instead of lopsided.
    ::v-deep .info-hint {
      margin: 0;
    }
  }

  &__app-section-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  &__device-row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 12px;
    background: rgba(125, 125, 125, 0.04);
    border: 1px solid rgba(125, 125, 125, 0.15);
    border-radius: 6px;
    margin-bottom: 6px;
    transition: border-color 0.12s ease;

    &:hover { border-color: rgba(125, 125, 125, 0.25); }
  }

  &__device-row-body {
    flex: 1;
    min-width: 0;
  }

  &__device-name {
    margin: 0;
    font-size: 13px;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.95);
  }

  // ─── Inline rename (mirrors ProjectTicket title pattern) ────────────
  &__device-name-display {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;
    padding: 2px 4px;
    margin: -2px -4px;
    border-radius: 4px;
    outline: none;
    transition: background 0.12s ease;

    &:hover,
    &:focus-visible {
      background: rgba(255, 255, 255, 0.03);
    }
  }

  &__device-name-pencil {
    font-size: 10px;
    color: rgba(255, 255, 255, 0.3);
    flex-shrink: 0;
    transition: color 0.15s ease;
  }

  &__device-name-display:hover &__device-name-pencil,
  &__device-name-display:focus-visible &__device-name-pencil {
    color: #e14eca;
  }

  &__device-name-edit {
    display: flex;
    align-items: center;
    gap: 2px;
    width: 100%;
  }

  &__device-name-input-wrap + &__device-name-icon {
    margin-left: 6px;
  }

  &__device-name-input-wrap {
    position: relative;
    display: flex;
    align-items: center;
    flex: 1 1 auto;
    max-width: 360px;
  }

  &__device-name-input {
    flex: 1;
    width: 100%;
    background: rgba(30, 30, 47, 0.4);
    color: #ffffff;
    border: 1px solid #e14eca;
    border-radius: 6px;
    padding: 8px 36px 8px 12px;
    font-size: 14px;
    font-weight: 500;
    line-height: 1.3;
    letter-spacing: -0.1px;
    font-family: inherit;
    outline: none;

    &:focus {
      outline: none;
      box-shadow: none;
      border-color: #e14eca;
      background: rgba(30, 30, 47, 0.4);
    }
  }

  &__device-name-kbd {
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 20px;
    height: 20px;
    padding: 0 5px;
    background: rgba(255, 255, 255, 0.08);
    border-radius: 4px;
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 12px;
    line-height: 1;
    color: rgba(255, 255, 255, 0.55);
    pointer-events: none;
  }

  &__device-name-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    background: transparent;
    border: none;
    border-radius: 6px;
    color: rgba(255, 255, 255, 0.5);
    cursor: pointer;
    padding: 0;
    transition: color 0.12s ease, background 0.12s ease;
    flex-shrink: 0;

    i { font-size: 15px; }

    &:hover { background: rgba(255, 255, 255, 0.06); color: rgba(255, 255, 255, 0.9); }
    &:focus-visible { outline: 2px solid #e14eca; outline-offset: 1px; }

    &--save {
      color: #e14eca;
      &:hover { background: rgba(225, 78, 202, 0.12); color: #e14eca; }
    }
  }

  &__device-meta {
    margin: 2px 0 0;
    font-size: 11px;
    color: rgba(255, 255, 255, 0.55);
    display: flex;
    align-items: center;
    gap: 6px;
  }

  &__device-dot { opacity: 0.5; }

  &__device-status {
    display: inline-block;
    font-weight: 600;

    &--active { color: #00f2c3; }
    &--offline { color: rgba(255, 255, 255, 0.55); }
    &--revoked { color: #fd5d93; }
  }

  &__device-row-actions {
    display: flex;
    gap: 4px;
  }

  &__devices-add-row {
    margin-top: 8px;
  }

  &__pair-form {
    margin-top: 12px;
    padding: 16px;
    background: rgba(255, 255, 255, 0.03);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 8px;
  }

  &__pair-form-title {
    margin: 0 0 14px;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: rgba(255, 255, 255, 0.85);
  }

  &__pair-form-name {
    margin-bottom: 14px;

    label { display: block; margin-bottom: 4px; }
  }

  &__pair-form-code {
    text-align: center;
    padding: 16px 12px;
    background: rgba(0, 0, 0, 0.18);
    border-radius: 6px;
    margin-bottom: 12px;
  }

  &__pair-code-label {
    margin: 0;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.55);
  }

  &__pair-code-value {
    margin: 6px 0 4px;
    font-size: 36px;
    font-weight: 700;
    letter-spacing: 0.06em;
    color: #ffffff;
    font-variant-numeric: tabular-nums;
  }

  &__pair-code-value--loading {
    color: rgba(255, 255, 255, 0.3);
    letter-spacing: 0.12em;
    font-size: 18px;
    line-height: 1.8;
  }

  &__pair-code-error {
    margin: 8px 0 0;
    font-size: 13px;
    color: #fd5d93;
    font-weight: 500;
  }

  &__pair-code-countdown {
    margin: 0;
    font-size: 12px;
    color: rgba(255, 255, 255, 0.6);
    font-variant-numeric: tabular-nums;

    &--low { color: #fd5d93; }
  }

  // Holds the tipping sub-options when the master toggle is on.
  &__tip-body {
    margin-top: 10px;
    display: flex;
    flex-direction: column;
    gap: 14px;

    // The field inputs and the shared label class each add their own margins on
    // top of the flex gap, doubling the spacing; the gap already separates every
    // block, so zero those margins (the 12px &__label top margin was the gap
    // above "Tip is calculated on").
    .ppe__row {
      margin-bottom: 0;
      ::v-deep .form-group { margin-bottom: 0; }
    }
    .ppe__label {
      margin-top: 0;
    }
  }

  // Calculation-base block (label + segmented-toggle + hint).
  &__tip-base {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  // Each on/off sub-feature reads as its own card.
  &__tip-subgroup {
    padding: 14px;
    border: 1px solid rgba(125, 125, 125, 0.18);
    border-radius: 8px;

    .white-content & {
      border-color: rgba(0, 0, 0, 0.1);
    }
  }

  &__tip-custom-type {
    margin-top: 10px;
  }

  &__tip-presets {
    margin-top: 12px;
  }

  &__tip-presets-empty {
    margin: 6px 0;
    padding: 10px 12px;
    background: rgba(255, 255, 255, 0.03);
    border: 1px dashed rgba(255, 255, 255, 0.12);
    border-radius: 4px;
    color: rgba(255, 255, 255, 0.5);
    font-size: 12px;
  }

  &__tip-presets-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  // Shared grid track so the header + every row align column-for-column.
  // Fixed, compact columns so the row sits left-aligned instead of stretching
  // full width: label, amount, the %/$ pills, remove.
  &__tip-preset-head,
  &__tip-preset-row {
    display: grid;
    grid-template-columns: 240px 100px auto 28px;
    gap: 10px;
    align-items: center;
  }

  &__tip-preset-row {
    padding: 2px 0;
  }

  &__tip-preset-remove {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    background: transparent;
    border: none;
    border-radius: 4px;
    color: rgba(255, 255, 255, 0.5);
    cursor: pointer;
    transition: color 0.12s ease, background 0.12s ease;
    i { font-size: 12px; }

    &:hover {
      background: rgba(225, 78, 202, 0.12);
      color: #e14eca;
    }
  }

  &__deferred {
    display: inline-flex;
    align-items: center;
    margin: 10px 0 0;
    padding: 6px 10px;
    background: rgba(225, 78, 202, 0.06);
    border: 1px solid rgba(225, 78, 202, 0.18);
    border-radius: 4px;
    color: rgba(255, 255, 255, 0.75);
    font-size: 11px;
    i { color: #e14eca; }
  }
}

// Trim padding inside the card body since we manage our own spacing
:deep(.ppe__card-body) {
  padding: 20px;

  @media (max-width: 575px) {
    padding: 14px;
  }
}
</style>

<style>
.white-content .ppe__label { color: rgba(0, 0, 0, 0.9); }
.white-content .ppe__textarea {
  background: rgba(255, 255, 255, 0.85);
  color: rgba(0, 0, 0, 0.9);
  border-color: rgba(225, 78, 202, 0.4);
}
.white-content .ppe__textarea::placeholder { color: rgba(0, 0, 0, 0.4); }
.white-content .ppe__type-readonly { color: rgba(0, 0, 0, 0.9); }
.white-content .ppe__deferred {
  color: rgba(0, 0, 0, 0.75);
  background: rgba(225, 78, 202, 0.06);
  border-color: rgba(225, 78, 202, 0.18);
}
.white-content .ppe__tip-presets-empty {
  background: rgba(0, 0, 0, 0.03);
  border-color: rgba(0, 0, 0, 0.12);
  color: rgba(0, 0, 0, 0.5);
}
.white-content .ppe__tip-preset-remove { color: rgba(0, 0, 0, 0.5); }
.white-content .ppe__tip-preset-remove:hover { color: #e14eca; }
.white-content .ppe__devices-empty {
  background: rgba(125, 125, 125, 0.04);
  border-color: rgba(0, 0, 0, 0.12);
}
.white-content .ppe__devices-empty-icon { color: rgba(0, 0, 0, 0.4); }
.white-content .ppe__devices-empty-title { color: rgba(0, 0, 0, 0.85); }
.white-content .ppe__devices-empty-hint { color: rgba(0, 0, 0, 0.6); }
.white-content .ppe__devices-subsection-title { color: rgba(0, 0, 0, 0.7); }
.white-content .ppe__device-type-chip {
  background: rgba(0, 0, 0, 0.06);
  color: rgba(0, 0, 0, 0.7);
}
.white-content .ppe__device-row {
  background: rgba(125, 125, 125, 0.04);
  border-color: rgba(0, 0, 0, 0.08);
}
.white-content .ppe__device-row:hover { border-color: rgba(0, 0, 0, 0.15); }
.white-content .ppe__device-name { color: rgba(0, 0, 0, 0.9); }
.white-content .ppe__device-name-display:hover,
.white-content .ppe__device-name-display:focus-visible {
  background: rgba(0, 0, 0, 0.04);
}
.white-content .ppe__device-name-pencil { color: rgba(0, 0, 0, 0.35); }
.white-content .ppe__device-name-display:hover .ppe__device-name-pencil,
.white-content .ppe__device-name-display:focus-visible .ppe__device-name-pencil {
  color: #e14eca;
}
.white-content .ppe__device-name-input {
  background: rgba(255, 255, 255, 0.85);
  color: rgba(0, 0, 0, 0.9);
}
.white-content .ppe__device-name-kbd {
  background: rgba(0, 0, 0, 0.06);
  color: rgba(0, 0, 0, 0.55);
}
.white-content .ppe__device-name-icon { color: rgba(0, 0, 0, 0.5); }
.white-content .ppe__device-name-icon:hover {
  background: rgba(0, 0, 0, 0.05);
  color: rgba(0, 0, 0, 0.9);
}
.white-content .ppe__device-name-icon--save { color: #e14eca; }
.white-content .ppe__device-meta { color: rgba(0, 0, 0, 0.55); }
.white-content .ppe__device-status--offline { color: rgba(0, 0, 0, 0.55); }
/* el-dropdown's root sets its own font/color; keep it from skewing the
   meta line's flex alignment. */
.ppe__device-meta ::v-deep .el-dropdown {
  display: inline-flex;
  align-items: center;
}
/* Pill-styled trigger — sized to sit beside the TABLET chip rather than
   inheriting el-dropdown's 14px link look. Explicit values because
   Element's global .el-dropdown font rules beat inherit chains. */
.ppe__device-autolock {
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 1px 8px;
  border-radius: 10px;
  background: rgba(225, 78, 202, 0.12);
  border: 1px solid rgba(225, 78, 202, 0.25);
  color: #e14eca;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.02em;
  line-height: 16px;
  white-space: nowrap;
}
.ppe__device-autolock:hover { background: rgba(225, 78, 202, 0.2); }
.ppe__device-autolock .tim-icons { font-size: 8px; }
.white-content .ppe__pair-form {
  background: rgba(0, 0, 0, 0.02);
  border-color: rgba(0, 0, 0, 0.08);
}
.white-content .ppe__pair-form-title { color: rgba(0, 0, 0, 0.85); }
.white-content .ppe__pair-form-code { background: rgba(0, 0, 0, 0.04); }
.white-content .ppe__pair-code-label { color: rgba(0, 0, 0, 0.55); }
.white-content .ppe__pair-code-value { color: rgba(0, 0, 0, 0.9); }
.white-content .ppe__pair-code-countdown { color: rgba(0, 0, 0, 0.6); }
</style>
